/**
 * Manager dashboard – toggle Manager / Employee view.
 * Manager view: default hours, bulk set, export, summary table, employee calendar.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import hrApi from './api';
import WorkingHoursCalendar from './components/WorkingHoursCalendar';
import HolidayCalendar from './components/HolidayCalendar';
import HolidayRequestForm from './components/HolidayRequestForm';
import HolidayRequestList from './components/HolidayRequestList';
import OvertimeRequestForm from './components/OvertimeRequestForm';
import OvertimeRequestList from './components/OvertimeRequestList';
import DaysTakenBreakdownModal from './components/DaysTakenBreakdownModal';
import DashboardAttendanceRoster from './components/manager/DashboardAttendanceRoster';
import WorkScheduleOverrides from './components/manager/WorkScheduleOverrides';
import useManagerDashboardData from './hooks/useManagerDashboardData';
import { publicHolidayDateSet, countWorkingDaysInRange } from './utils/workingDays';
import { formatDecimalHours, formatHHmmToAmPm, formatDisplayName } from './utils/formatTime';
import './css/EmployeeDashboard.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const VIEW_MANAGER = 'manager';
const VIEW_EMPLOYEE = 'employee';

function displayOvertimeReasonForView(r) {
  if (!r) return '–';
  if (r.reason != null && String(r.reason).trim()) return String(r.reason);
  const legacy = [r.comment, r.fromTime && r.toTime ? `${r.fromTime}–${r.toTime}` : null].filter(Boolean).join(' · ');
  return legacy || '–';
}

function toDateInputValue(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeekMonday(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function endOfWeekSunday(date) {
  const start = startOfWeekMonday(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

function parseHHmm(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.trim().split(':').map(Number);
  if (parts.length >= 2) return parts[0] + parts[1] / 60;
  return null;
}

/** Inferred break duration when only start/end/total are stored (matches EmployeeDashboard). */
function getDerivedBreakHours(entry) {
  if (!entry || entry.entryMode !== 'time' || !entry.startTime || !entry.endTime || entry.totalHours == null) return null;
  const startH = parseHHmm(entry.startTime);
  const endH = parseHHmm(entry.endTime);
  if (startH == null || endH == null || endH <= startH) return null;
  const breakH = Math.round((endH - startH - (Number(entry.totalHours) || 0)) * 100) / 100;
  return breakH > 0 ? breakH : null;
}

function hoursToHHmm(hours) {
  if (hours == null || Number.isNaN(hours)) return '';
  const capped = hours < 0 ? 0 : hours >= 24 ? 23 + 59 / 60 : hours;
  const h = Math.floor(capped);
  const m = Math.round((capped - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function endOfMonth(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  d.setMonth(d.getMonth() + 1, 0);
  return d;
}

function formatDateShort(date) {
  return new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function ManagerDashboard({ isAdminView = false, onSwitchToAdmin = null }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dn = (u) => formatDisplayName(u || '');
  const now = new Date();
  const initialWeekStart = startOfWeekMonday(now);
  const initialWeekEnd = new Date(initialWeekStart);
  initialWeekEnd.setDate(initialWeekEnd.getDate() + 6);
  const [viewMode, setViewMode] = useState(VIEW_MANAGER);
  const [activeSection, setActiveSection] = useState('overview');
  const [employees, setEmployees] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState('');
  const [defaultHours, setDefaultHours] = useState(null);
  const [defaultForm, setDefaultForm] = useState({
    defaultStartTime: '08:00',
    defaultEndTime: '17:12',
    defaultTotalHours: 8.2,
    breakStartTime: '12:00',
    breakEndTime: '13:00',
  });
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [teamTab, setTeamTab] = useState('dashboard');
  const [calendarEmployeeId, setCalendarEmployeeId] = useState(() => {
    try {
    return localStorage.getItem('staff-portal-calendar-employee-id') || '';
    } catch {
      return '';
    }
  });
  const firstDayOfMonth = (y, m) => `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDayOfMonth = (y, m) => {
    const d = new Date(y, m, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [summaryStartDate, setSummaryStartDate] = useState(() => firstDayOfMonth(now.getFullYear(), now.getMonth() + 1));
  const [summaryEndDate, setSummaryEndDate] = useState(() => lastDayOfMonth(now.getFullYear(), now.getMonth() + 1));
  const [summaryData, setSummaryData] = useState({ list: [], grandTotal: 0, daysInRange: 0 });
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(() => firstDayOfMonth(now.getFullYear(), now.getMonth() + 1));
  const [exportEndDate, setExportEndDate] = useState(() => lastDayOfMonth(now.getFullYear(), now.getMonth() + 1));
  const [exportAdminIds, setExportAdminIds] = useState([]);
  const [exportReportType, setExportReportType] = useState('daily');
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [showEmployeeDefaultsModal, setShowEmployeeDefaultsModal] = useState(false);
  const [employeeDefaultForm, setEmployeeDefaultForm] = useState({
    defaultStartTime: '08:00',
    defaultEndTime: '17:12',
    defaultTotalHours: 8.2,
    workingDays: [1, 2, 3, 4, 5],
    daySchedule: { 1: 'full', 2: 'full', 3: 'full', 4: 'full', 5: 'full' },
    breakStartTime: '12:00',
    breakEndTime: '13:00',
  });
  const [settingsEmployeeId, setSettingsEmployeeId] = useState(null);
  const [savingEmployeeDefaults, setSavingEmployeeDefaults] = useState(false);
  const [loadingEmployeeDefaults, setLoadingEmployeeDefaults] = useState(false);
  const [employeeDefaultsIsCustom, setEmployeeDefaultsIsCustom] = useState(false);
  const [showExportEmployeesModal, setShowExportEmployeesModal] = useState(false);
  const [exportModalSelectedIds, setExportModalSelectedIds] = useState([]);
  const [defaultsRefreshKey, setDefaultsRefreshKey] = useState(0);
  const [holidayRequests, setHolidayRequests] = useState([]);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [holidayError, setHolidayError] = useState('');
  const [holidaySuccessMessage, setHolidaySuccessMessage] = useState('');
  const [showBalanceBreakdown, setShowBalanceBreakdown] = useState(false);
  const [overtimeEmployeeRequests, setOvertimeEmployeeRequests] = useState([]);
  const [overtimeEmployeeLoading, setOvertimeEmployeeLoading] = useState(false);
  const [overtimeEmployeeError, setOvertimeEmployeeError] = useState('');
  const [overtimeEmployeeSuccessMessage, setOvertimeEmployeeSuccessMessage] = useState('');
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState('');
  const [overviewRangePreset, setOverviewRangePreset] = useState('this_week');
  const [overviewPublicHolidays, setOverviewPublicHolidays] = useState([]);
  const [overviewWorkSettings, setOverviewWorkSettings] = useState(null);
  const [overviewWorkingEntries, setOverviewWorkingEntries] = useState([]);
  const [allowance, setAllowance] = useState(null);
  const [allowanceYear, setAllowanceYear] = useState(new Date().getFullYear());
  const [pendingHolidays, setPendingHolidays] = useState([]);
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [overtimeRejectModal, setOvertimeRejectModal] = useState(null);
  const [overtimeRejectReason, setOvertimeRejectReason] = useState('');
  const [overtimeApproveModal, setOvertimeApproveModal] = useState(null);
  const [overtimeApproveDays, setOvertimeApproveDays] = useState('');
  const [overtimeViewRequest, setOvertimeViewRequest] = useState(null);
  const [overtimeDecisionMessage, setOvertimeDecisionMessage] = useState('');
  const [overtimeDecisionError, setOvertimeDecisionError] = useState('');
  const [overtimeDecisionLoadingId, setOvertimeDecisionLoadingId] = useState(null);
  const [holidayRejectModal, setHolidayRejectModal] = useState(null);
  const [holidayEditModal, setHolidayEditModal] = useState(null);
  const [holidayViewRequest, setHolidayViewRequest] = useState(null);
  const [holidayRejectReason, setHolidayRejectReason] = useState('');
  const [viewModalRejectReason, setViewModalRejectReason] = useState('');
  const [approvalPublicHolidays, setApprovalPublicHolidays] = useState([]);
  /** adminId (string) → { workingDays, daySchedule } for leave day counts on Holiday approvals */
  const [approvalEmployeeSchedules, setApprovalEmployeeSchedules] = useState({});
  /** adminId (string) → override windows[] for leave-day calculations in approvals. */
  const [approvalEmployeeOverrides, setApprovalEmployeeOverrides] = useState({});
  const [approvalFilterEmployee, setApprovalFilterEmployee] = useState('');
  const [approvalFilterStatus, setApprovalFilterStatus] = useState('');
  const [approvalFilterDateFrom, setApprovalFilterDateFrom] = useState('');
  const [approvalFilterDateTo, setApprovalFilterDateTo] = useState('');
  const [overtimeFilterEmployee, setOvertimeFilterEmployee] = useState('');
  const [overtimeFilterStatus, setOvertimeFilterStatus] = useState('');
  const [overtimeFilterDateFrom, setOvertimeFilterDateFrom] = useState('');
  const [overtimeFilterDateTo, setOvertimeFilterDateTo] = useState('');
  const [holidayApprovalsRefreshKey, setHolidayApprovalsRefreshKey] = useState(0);
  const [publicHolidayYear, setPublicHolidayYear] = useState(2026);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [loadingPublicHolidays, setLoadingPublicHolidays] = useState(false);
  const [publicHolidayModal, setPublicHolidayModal] = useState(null);
  const [publicHolidayForm, setPublicHolidayForm] = useState({ name: '', nameFr: '', date: '', endDate: '' });
  const [savingPublicHoliday, setSavingPublicHoliday] = useState(false);
  const [allowanceListYear, setAllowanceListYear] = useState(new Date().getFullYear());
  const [allowanceList, setAllowanceList] = useState([]);
  const [loadingAllowanceList, setLoadingAllowanceList] = useState(false);
  const [showEmployeeAllowanceModal, setShowEmployeeAllowanceModal] = useState(false);
  const [loadingEmployeeAllowance, setLoadingEmployeeAllowance] = useState(false);
  const [employeeAllowanceForm, setEmployeeAllowanceForm] = useState({ year: new Date().getFullYear(), allowances: [] });
  const [employeeAllowanceData, setEmployeeAllowanceData] = useState(null);
  const [savingEmployeeAllowance, setSavingEmployeeAllowance] = useState(false);
  const [allowanceEmployeeId, setAllowanceEmployeeId] = useState('');
  const [editingAllowanceEmployeeId, setEditingAllowanceEmployeeId] = useState(null);
  const [showDefaultAllowanceModal, setShowDefaultAllowanceModal] = useState(false);
  const [showPublicHolidayManageModal, setShowPublicHolidayManageModal] = useState(false);
  const [publicHolidaysRefreshKey, setPublicHolidaysRefreshKey] = useState(0);
  const [defaultAllowanceForm, setDefaultAllowanceForm] = useState({ year: new Date().getFullYear(), allowances: [] });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showLeaveTypesModal, setShowLeaveTypesModal] = useState(false);
  const [newLeaveTypeName, setNewLeaveTypeName] = useState('');
  const [savingLeaveType, setSavingLeaveType] = useState(false);
  const [loadingDefaultAllowance, setLoadingDefaultAllowance] = useState(false);
  const [savingDefaultAllowance, setSavingDefaultAllowance] = useState(false);
  const [expandedAllowanceEmployeeId, setExpandedAllowanceEmployeeId] = useState(null);
  const [me, setMe] = useState(null);
  const [assistantManagers, setAssistantManagers] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [newAssistantId, setNewAssistantId] = useState('');
  const [newAssistantPermissions, setNewAssistantPermissions] = useState(['approve_holiday']);
  const [savingAssistant, setSavingAssistant] = useState(false);
  const [actAsModal, setActAsModal] = useState({
    show: false, action: null, holidayId: null, rejectReason: null, approvedDays: undefined,
  });
  const [activityDecisions, setActivityDecisions] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ username: '', email: '', password: '', role: 'employee' });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const [dashboardPreset, setDashboardPreset] = useState('this_week');
  const [dashboardStartDate, setDashboardStartDate] = useState(() => toDateInputValue(initialWeekStart));
  const [dashboardEndDate, setDashboardEndDate] = useState(() => toDateInputValue(initialWeekEnd));

  const {
    data: managerDashboardData,
    loading: managerDashboardLoading,
    refreshing: managerDashboardRefreshing,
    error: managerDashboardError,
    weekLabel: managerDashboardWeekLabel,
  } = useManagerDashboardData({
    enabled: viewMode === VIEW_MANAGER && teamTab === 'dashboard',
    refreshKey: holidayApprovalsRefreshKey,
    startDate: dashboardStartDate,
    endDate: dashboardEndDate,
  });

  const [dashboardPublicHolidays, setDashboardPublicHolidays] = useState([]);
  useEffect(() => {
    if (!dashboardStartDate || !dashboardEndDate) return;
    const startYear = new Date(dashboardStartDate + 'T00:00:00').getFullYear();
    const endYear = new Date(dashboardEndDate + 'T00:00:00').getFullYear();
    const years = startYear === endYear ? [startYear] : [startYear, endYear];
    Promise.all(
      years.map((year) =>
        hrApi.get('/api/public-holidays', { params: { year } }).then((res) => (Array.isArray(res.data) ? res.data : []))
      )
    )
      .then((lists) => setDashboardPublicHolidays(lists.flat()))
      .catch(() => setDashboardPublicHolidays([]));
  }, [dashboardStartDate, dashboardEndDate]);

  const patchedWeeklyAvailability = useMemo(() => {
    const days = managerDashboardData.weeklyAvailability || [];
    if (!dashboardPublicHolidays.length) return days;
    const phByDate = {};
    dashboardPublicHolidays.forEach((ph) => {
      if (!ph?.date) return;
      const start = new Date(ph.date);
      start.setHours(0, 0, 0, 0);
      const end = ph.endDate ? new Date(ph.endDate) : new Date(ph.date);
      end.setHours(0, 0, 0, 0);
      const label = ph.name || ph.nameFr || 'Public holiday';
      const d = new Date(start);
      while (d <= end) {
        phByDate[toDateInputValue(d)] = label;
        d.setDate(d.getDate() + 1);
      }
    });
    return days.map((day) => {
      const isPublicHoliday = !!phByDate[day.date];
      const publicHolidayNames = isPublicHoliday ? [phByDate[day.date]] : [];
      if (isPublicHoliday === day.isPublicHoliday) return day;
      return { ...day, isPublicHoliday, publicHolidayNames, isWorkingDay: !day.isWeekend && !isPublicHoliday };
    });
  }, [managerDashboardData.weeklyAvailability, dashboardPublicHolidays]);

  const CALENDAR_EMPLOYEE_STORAGE_KEY = 'staff-portal-calendar-employee-id';

  const formatDecidedBy = (r) => {
    if (!r) return '–';
    const statusOrAction = r.status ?? r.action;
    if (statusOrAction !== 'approved' && statusOrAction !== 'rejected') return '–';
    const by = r.approvedBy && typeof r.approvedBy === 'object' ? r.approvedBy.username : null;
    const performed = r.performedBy && typeof r.performedBy === 'object' ? r.performedBy.username : null;
    if (!by) return '–';
    if (performed && performed !== by) return `By ${by} (performed by ${performed})`;
    return `By ${by}`;
  };

  const formatAuthority = (r) => {
    if (!r) return '–';
    const by = r.approvedBy && typeof r.approvedBy === 'object' ? r.approvedBy.username : null;
    return by || '–';
  };

  const formatPerformedBy = (r) => {
    if (!r) return '–';
    const by = r.approvedBy && typeof r.approvedBy === 'object' ? r.approvedBy.username : null;
    const performed = r.performedBy && typeof r.performedBy === 'object' ? r.performedBy.username : null;
    if (performed && performed !== by) return performed;
    return '–';
  };

  useEffect(() => {
    let cancelled = false;
    hrApi.get('/api/me').then((res) => {
      if (!cancelled) setMe(res.data);
    }).catch(() => { if (!cancelled) setMe(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const role = String(me?.role || '').toLowerCase();
    if (!role) return;
    // Block employee accounts from accessing manager dashboard route.
    if (role === 'employee') {
      navigate('/employee', { replace: true });
    }
  }, [me?.role, navigate]);

  useEffect(() => {
    if (me?.role !== 'manager') return;
    let cancelled = false;
    setLoadingAssistants(true);
    hrApi.get('/api/managers/me/assistants')
      .then((res) => { if (!cancelled) setAssistantManagers(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setAssistantManagers([]); })
      .finally(() => { if (!cancelled) setLoadingAssistants(false); });
    return () => { cancelled = true; };
  }, [me?.role]);

  useEffect(() => {
    let cancelled = false;
    hrApi
      .get('/api/employees')
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) {
          setEmployees(res.data);
          if (res.data.length > 0) {
            setCalendarEmployeeId((prev) => {
              const valid = prev && res.data.some((e) => e._id === prev);
              const next = valid ? prev : res.data[0]._id;
              try {
                if (next) localStorage.setItem(CALENDAR_EMPLOYEE_STORAGE_KEY, next);
              } catch (_) {}
              return next;
            });
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const status = err?.response?.status;
          const msg = err?.response?.data?.message;
          if (status === 403 && msg) {
            setError(msg + ' (Only managers can view the team list.)');
          } else if (status === 404) {
            setError('Not found – restart the HR backend (cd igf-hr/backend && npm start) and refresh.');
          } else {
            setError(msg || 'Failed to load employees. Check the backend is running.');
          }
          setEmployees([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    hrApi.get('/api/default-working-hours', { params: { global: 'true' } }).then((res) => {
      setDefaultHours(res.data);
      setDefaultForm({
        defaultStartTime: res.data.defaultStartTime || '08:00',
        defaultEndTime: res.data.defaultEndTime || '17:12',
        defaultTotalHours: res.data.defaultTotalHours != null ? res.data.defaultTotalHours : 8.2,
        breakStartTime: res.data.breakStartTime ?? '12:00',
        breakEndTime: res.data.breakEndTime ?? '13:00',
      });
    }).catch(() => setDefaultHours(null));
  }, []);

  useEffect(() => {
    if (!summaryStartDate || !summaryEndDate) return;
    setLoadingSummary(true);
    hrApi.get('/api/working-hours/summary', { params: { startDate: summaryStartDate, endDate: summaryEndDate } })
      .then((res) => {
        if (res.data && Array.isArray(res.data.list)) {
          setSummaryData({
            list: res.data.list,
            grandTotal: res.data.grandTotal != null ? res.data.grandTotal : 0,
            daysInRange: res.data.daysInRange != null ? res.data.daysInRange : 0,
          });
        } else {
          setSummaryData({ list: [], grandTotal: 0, daysInRange: 0 });
        }
      })
      .catch(() => setSummaryData({ list: [], grandTotal: 0, daysInRange: 0 }))
      .finally(() => setLoadingSummary(false));
  }, [summaryStartDate, summaryEndDate]);

  const handleDefaultStartChange = (e) => {
    const newStart = e.target.value;
    setDefaultForm((f) => {
      const s = parseHHmm(newStart);
      const endH = parseHHmm(f.defaultEndTime);
      const total = typeof f.defaultTotalHours === 'number' ? f.defaultTotalHours : 8;
      if (s != null && endH != null && endH > s) {
        return { ...f, defaultStartTime: newStart, defaultTotalHours: Math.round((endH - s) * 100) / 100 };
      }
      if (s != null) {
        return { ...f, defaultStartTime: newStart, defaultEndTime: hoursToHHmm(s + total) };
      }
      return { ...f, defaultStartTime: newStart };
    });
  };

  const handleDefaultEndChange = (e) => {
    const newEnd = e.target.value;
    setDefaultForm((f) => {
      const s = parseHHmm(f.defaultStartTime);
      const e = parseHHmm(newEnd);
      if (s != null && e != null && e > s) {
        return { ...f, defaultEndTime: newEnd, defaultTotalHours: Math.round((e - s) * 100) / 100 };
      }
      return { ...f, defaultEndTime: newEnd };
    });
  };

  const handleDefaultTotalChange = (e) => {
    const val = parseFloat(e.target.value, 10);
    const total = Number.isNaN(val) || val < 0 ? 0 : Math.min(24, val);
    setDefaultForm((f) => {
      const s = parseHHmm(f.defaultStartTime);
      if (s != null) {
        return { ...f, defaultTotalHours: total, defaultEndTime: hoursToHHmm(s + total) };
      }
      return { ...f, defaultTotalHours: total };
    });
  };

  const handleAddUser = (e) => {
    e.preventDefault();
    setAddUserError('');
    setAddUserLoading(true);
    const createdRole = addUserForm.role;
    hrApi.post('/api/employees', addUserForm)
      .then(() => {
        setShowAddUserModal(false);
        setAddUserForm({ username: '', email: '', password: '', role: 'employee' });
        return Promise.all([
          hrApi.get('/api/employees'),
          createdRole === 'assistant_manager' && me?.role === 'manager' ? hrApi.get('/api/managers/me/assistants') : Promise.resolve(null),
        ]);
      })
      .then(([employeesRes, assistantsRes]) => {
        if (employeesRes && Array.isArray(employeesRes.data)) setEmployees(employeesRes.data);
        if (assistantsRes && Array.isArray(assistantsRes.data)) setAssistantManagers(assistantsRes.data);
      })
      .catch((err) => {
        setAddUserError(err?.response?.data?.message || 'Failed to create user');
      })
      .finally(() => setAddUserLoading(false));
  };

  const handleSaveDefaults = (e) => {
    e.preventDefault();
    setSavingDefaults(true);
    const payload = {
      defaultStartTime: defaultForm.defaultStartTime,
      defaultEndTime: defaultForm.defaultEndTime,
      defaultTotalHours: defaultForm.defaultTotalHours,
      breakStartTime: defaultForm.breakStartTime === '' ? null : defaultForm.breakStartTime,
      breakEndTime: defaultForm.breakEndTime === '' ? null : defaultForm.breakEndTime,
    };
    hrApi.put('/api/default-working-hours', payload)
      .then((res) => {
        setDefaultHours(res.data);
        setError('');
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to save defaults'))
      .finally(() => setSavingDefaults(false));
  };

  const openEmployeeDefaultsModal = (employeeIdFromSettings) => {
    const employeeId = (employeeIdFromSettings || settingsEmployeeId || calendarEmployeeId) && String(employeeIdFromSettings || settingsEmployeeId || calendarEmployeeId).trim();
    if (!employeeId) return;
    if (employeeIdFromSettings) setSettingsEmployeeId(employeeIdFromSettings);
    setError('');
    setLoadingEmployeeDefaults(true);
    setShowEmployeeDefaultsModal(true);
    hrApi.get(`/api/employees/${employeeId}/default-working-hours`)
      .then((res) => {
        const wd = res.data.workingDays;
        const workingDays = Array.isArray(wd) && wd.length > 0 ? wd : [1, 2, 3, 4, 5];
        let daySchedule = res.data.daySchedule && typeof res.data.daySchedule === 'object' ? { ...res.data.daySchedule } : null;
        if (!daySchedule) {
          daySchedule = { 1: 'full', 2: 'full', 3: 'full', 4: 'full', 5: 'full' };
          [1, 2, 3, 4, 5].forEach((d) => { if (!workingDays.includes(d)) daySchedule[d] = 'off'; });
        }
        ['1', '2', '3', '4', '5'].forEach((k) => { if (!['off', 'full', 'morning', 'afternoon'].includes(daySchedule[k])) daySchedule[k] = workingDays.includes(Number(k)) ? 'full' : 'off'; });
        const isOverridden = !!res.data.isOverridden;
        setEmployeeDefaultForm({
          defaultStartTime: res.data.defaultStartTime || '08:00',
          defaultEndTime: res.data.defaultEndTime || '17:12',
          defaultTotalHours: res.data.defaultTotalHours != null ? res.data.defaultTotalHours : 8.2,
          workingDays,
          daySchedule,
          breakStartTime: res.data.breakStartTime ?? '12:00',
          breakEndTime: res.data.breakEndTime ?? '13:00',
        });
        setEmployeeDefaultsIsCustom(isOverridden);
      })
      .catch(() => setError('Failed to load employee defaults'))
      .finally(() => setLoadingEmployeeDefaults(false));
  };

  const handleEmployeeDefaultStartChange = (e) => {
    const newStart = e.target.value;
    setEmployeeDefaultForm((f) => {
      const s = parseHHmm(newStart);
      const endH = parseHHmm(f.defaultEndTime);
      const total = typeof f.defaultTotalHours === 'number' ? f.defaultTotalHours : 8;
      if (s != null && endH != null && endH > s) {
        return { ...f, defaultStartTime: newStart, defaultTotalHours: Math.round((endH - s) * 100) / 100 };
      }
      if (s != null) return { ...f, defaultStartTime: newStart, defaultEndTime: hoursToHHmm(s + total) };
      return { ...f, defaultStartTime: newStart };
    });
  };

  const handleEmployeeDefaultEndChange = (e) => {
    const newEnd = e.target.value;
    setEmployeeDefaultForm((f) => {
      const s = parseHHmm(f.defaultStartTime);
      const e = parseHHmm(newEnd);
      if (s != null && e != null && e > s) {
        return { ...f, defaultEndTime: newEnd, defaultTotalHours: Math.round((e - s) * 100) / 100 };
      }
      return { ...f, defaultEndTime: newEnd };
    });
  };

  const handleEmployeeDefaultTotalChange = (e) => {
    const val = parseFloat(e.target.value, 10);
    const total = Number.isNaN(val) || val < 0 ? 0 : Math.min(24, val);
    setEmployeeDefaultForm((f) => {
      const s = parseHHmm(f.defaultStartTime);
      if (s != null) return { ...f, defaultTotalHours: total, defaultEndTime: hoursToHHmm(s + total) };
      return { ...f, defaultTotalHours: total };
    });
  };

  const setEmployeeDaySchedule = (day, value) => {
    setEmployeeDefaultForm((f) => {
      const next = { ...(f.daySchedule || { 1: 'full', 2: 'full', 3: 'full', 4: 'full', 5: 'full' }) };
      next[String(day)] = value;
      const workingDays = [1, 2, 3, 4, 5].filter((d) => next[String(d)] !== 'off');
      return { ...f, daySchedule: next, workingDays: workingDays.length ? workingDays : [1, 2, 3, 4, 5] };
    });
  };

  const handleSaveEmployeeDefaults = (e) => {
    e.preventDefault();
    const employeeIdToSave = (settingsEmployeeId || calendarEmployeeId) && String(settingsEmployeeId || calendarEmployeeId).trim();
    if (!employeeIdToSave) {
      setError('Please select an employee.');
      return;
    }
    setSavingEmployeeDefaults(true);
    setError('');
    const { defaultStartTime, defaultEndTime, defaultTotalHours, daySchedule, breakStartTime, breakEndTime } = employeeDefaultForm;
    const payload = {
      defaultStartTime,
      defaultEndTime,
      defaultTotalHours,
      daySchedule: daySchedule && typeof daySchedule === 'object' ? daySchedule : undefined,
      breakStartTime: breakStartTime === '' ? null : breakStartTime,
      breakEndTime: breakEndTime === '' ? null : breakEndTime,
    };
    hrApi.put(`/api/employees/${employeeIdToSave}/default-working-hours`, payload)
      .then(() => {
        setEmployeeDefaultsIsCustom(true);
        setError('');
        setShowEmployeeDefaultsModal(false);
        setSettingsEmployeeId(null);
        setDefaultsRefreshKey((k) => k + 1);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to save'))
      .finally(() => setSavingEmployeeDefaults(false));
  };

  const handleResetEmployeeDefaultsToGlobal = () => {
    const employeeId = (settingsEmployeeId || calendarEmployeeId) && String(settingsEmployeeId || calendarEmployeeId).trim();
    if (!employeeId || !window.confirm('Remove override? This employee will use the global default hours.')) return;
    setSavingEmployeeDefaults(true);
    setError('');
    hrApi.delete(`/api/employees/${employeeId}/default-working-hours`)
      .then(() => {
        setEmployeeDefaultsIsCustom(false);
        setError('');
        setShowEmployeeDefaultsModal(false);
        setDefaultsRefreshKey((k) => k + 1);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to reset'))
      .finally(() => setSavingEmployeeDefaults(false));
  };

  const fetchMyHolidays = () => {
    hrApi.get('/api/holidays').then((res) => setHolidayRequests(Array.isArray(res.data) ? res.data : [])).catch(() => setHolidayRequests([]));
  };
  const fetchMyOvertime = () => {
    hrApi.get('/api/overtime').then((res) => setOvertimeEmployeeRequests(Array.isArray(res.data) ? res.data : [])).catch(() => setOvertimeEmployeeRequests([]));
  };
  const fetchMyOverview = () => {
    setOverviewLoading(true);
    setOverviewError('');
    const today = new Date();
    const currentYear = today.getFullYear();
    const weekStart = startOfWeekMonday(today);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const nextMonthEnd = endOfMonth(new Date(today.getFullYear(), today.getMonth() + 1, 1));
    const rangeStart = weekStart < monthStart ? weekStart : monthStart;
    const startDate = toDateInputValue(rangeStart);
    const endDate = toDateInputValue(nextMonthEnd);
    Promise.all([
      hrApi.get('/api/holidays'),
      hrApi.get('/api/overtime'),
      hrApi.get('/api/holidays/allowance', { params: { year: currentYear } }),
      hrApi.get('/api/default-working-hours'),
      hrApi.get('/api/working-hours', { params: { startDate, endDate } }),
    ])
      .then(([holidaysRes, overtimeRes, allowanceRes, workSettingsRes, workingRes]) => {
        setHolidayRequests(Array.isArray(holidaysRes.data) ? holidaysRes.data : []);
        setOvertimeEmployeeRequests(Array.isArray(overtimeRes.data) ? overtimeRes.data : []);
        setAllowance(allowanceRes.data || null);
        setOverviewWorkSettings(workSettingsRes?.data || null);
        setOverviewWorkingEntries(Array.isArray(workingRes.data) ? workingRes.data : []);
      })
      .catch(() => setOverviewError('Failed to load overview data.'))
      .finally(() => setOverviewLoading(false));
  };

  useEffect(() => {
    if (viewMode === VIEW_EMPLOYEE && activeSection === 'overview') fetchMyOverview();
    if (viewMode === VIEW_EMPLOYEE && activeSection === 'holidays') fetchMyHolidays();
    if (viewMode === VIEW_EMPLOYEE && activeSection === 'overtime') fetchMyOvertime();
  }, [viewMode, activeSection]);

  useEffect(() => {
    if (viewMode === VIEW_EMPLOYEE && (activeSection === 'holidays' || activeSection === 'overview')) {
      hrApi.get('/api/holidays/allowance', { params: { year: allowanceYear } })
        .then((res) => setAllowance(res.data))
        .catch(() => setAllowance(null));
    }
  }, [viewMode, activeSection, allowanceYear]);

  useEffect(() => {
    if (viewMode === VIEW_MANAGER && teamTab === 'holiday-approvals') {
      hrApi.get('/api/holidays', { params: { status: 'all' } })
        .then((res) => setPendingHolidays(Array.isArray(res.data) ? res.data : []))
        .catch(() => setPendingHolidays([]));
    }
  }, [viewMode, teamTab, holidayApprovalsRefreshKey]);

  useEffect(() => {
    if (viewMode === VIEW_MANAGER && teamTab === 'overtime-approvals') {
      setOvertimeDecisionError('');
      hrApi.get('/api/overtime', { params: { status: 'all' } })
        .then((res) => setOvertimeRequests(Array.isArray(res.data) ? res.data : []))
        .catch(() => setOvertimeRequests([]));
    }
  }, [viewMode, teamTab, holidayApprovalsRefreshKey]);

  useEffect(() => {
    if (viewMode !== VIEW_MANAGER || teamTab !== 'activity') return;
    setLoadingActivity(true);
    hrApi.get('/api/activity', { params: { limit: 100 } })
      .then((res) => setActivityDecisions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setActivityDecisions([]))
      .finally(() => setLoadingActivity(false));
  }, [viewMode, teamTab, holidayApprovalsRefreshKey]);

  const approvalYears = useMemo(() => {
    const years = new Set();
    pendingHolidays.forEach((r) => {
      if (r.startDate) years.add(new Date(r.startDate).getFullYear());
      if (r.endDate) years.add(new Date(r.endDate).getFullYear());
    });
    return Array.from(years);
  }, [pendingHolidays]);

  useEffect(() => {
    if (viewMode !== VIEW_MANAGER || teamTab !== 'holiday-approvals' || approvalYears.length === 0) {
      setApprovalPublicHolidays([]);
      return;
    }
    const fetches = approvalYears.map((year) =>
      hrApi.get('/api/public-holidays', { params: { year } }).then((res) => ({ year, list: Array.isArray(res.data) ? res.data : [] }))
    );
    Promise.all(fetches).then((results) => {
      const list = results.flatMap((r) => r.list);
      setApprovalPublicHolidays(list);
    }).catch(() => setApprovalPublicHolidays([]));
  }, [viewMode, teamTab, approvalYears.join(',')]);

  useEffect(() => {
    if (viewMode !== VIEW_MANAGER || teamTab !== 'holiday-approvals' || !pendingHolidays.length) {
      setApprovalEmployeeSchedules({});
      return;
    }
    const ids = new Set();
    pendingHolidays.forEach((r) => {
      const raw = r.adminId && typeof r.adminId === 'object' && r.adminId._id != null
        ? r.adminId._id.toString()
        : (r.adminId != null ? String(r.adminId) : '');
      if (raw && /^[a-f\d]{24}$/i.test(raw)) ids.add(raw);
    });
    if (ids.size === 0) {
      setApprovalEmployeeSchedules({});
      setApprovalEmployeeOverrides({});
      return;
    }
    const adminIds = Array.from(ids).join(',');
    Promise.all([
      hrApi.get('/api/default-working-hours/batch', { params: { adminIds } }),
      hrApi.get('/api/work-schedule-overrides'),
    ])
      .then(([schedRes, overridesRes]) => {
        setApprovalEmployeeSchedules(schedRes.data && typeof schedRes.data === 'object' ? schedRes.data : {});
        const grouped = {};
        const list = Array.isArray(overridesRes.data) ? overridesRes.data : [];
        list.forEach((ov) => {
          const employeeRules = Array.isArray(ov.employeeRules) ? ov.employeeRules : [];
          employeeRules.forEach((rule) => {
            const aid = String(rule?.adminId || '');
            if (!aid || !ids.has(aid)) return;
            if (!grouped[aid]) grouped[aid] = [];
            grouped[aid].push({
              adminId: aid,
              startDate: ov.startDate,
              endDate: ov.endDate,
              mode: rule.mode || 'full_time',
              hoursPerDay: rule.hoursPerDay,
              workingDays: rule.workingDays,
              updatedAt: ov.updatedAt,
              createdAt: ov.createdAt,
            });
          });
        });
        setApprovalEmployeeOverrides(grouped);
      })
      .catch(() => {
        setApprovalEmployeeSchedules({});
        setApprovalEmployeeOverrides({});
      });
  }, [viewMode, teamTab, pendingHolidays, holidayApprovalsRefreshKey]);

  const holidayRequestAdminIdStr = (r) => {
    if (!r?.adminId) return '';
    if (typeof r.adminId === 'object' && r.adminId._id != null) return r.adminId._id.toString();
    return String(r.adminId);
  };

  const getDaysTaken = (r) => {
    if (!r?.startDate || !r?.endDate) return '–';
    if (r.halfDay === 'am' || r.halfDay === 'pm') return 0.5;
    const start = new Date(r.startDate);
    const end = new Date(r.endDate);
    if (end < start) return 0;
    const set = publicHolidayDateSet(approvalPublicHolidays);
    const aid = holidayRequestAdminIdStr(r);
    const sched = aid && approvalEmployeeSchedules[aid] ? approvalEmployeeSchedules[aid] : null;
    const overrides = aid && approvalEmployeeOverrides[aid] ? approvalEmployeeOverrides[aid] : null;
    return countWorkingDaysInRange(start, end, set, sched, overrides, aid);
  };

  const filteredApprovalHolidays = useMemo(() => {
    let list = pendingHolidays || [];
    const emp = (approvalFilterEmployee || '').trim().toLowerCase();
    if (emp) {
      list = list.filter((r) => {
        const name = r.adminId && typeof r.adminId === 'object' ? (r.adminId.username || '').toLowerCase() : String(r.adminId || '').toLowerCase();
        return name.includes(emp);
      });
    }
    if (approvalFilterStatus) {
      list = list.filter((r) => r.status === approvalFilterStatus);
    }
    if (approvalFilterDateFrom || approvalFilterDateTo) {
      const from = approvalFilterDateFrom ? new Date(approvalFilterDateFrom) : null;
      const to = approvalFilterDateTo ? new Date(approvalFilterDateTo) : null;
      if (from || to) {
        list = list.filter((r) => {
          const reqStart = r.startDate ? new Date(r.startDate) : null;
          const reqEnd = r.endDate ? new Date(r.endDate) : null;
          if (!reqStart || !reqEnd) return false;
          if (from && reqEnd < from) return false;
          if (to && reqStart > to) return false;
          return true;
        });
      }
    }
    return list;
  }, [pendingHolidays, approvalFilterEmployee, approvalFilterStatus, approvalFilterDateFrom, approvalFilterDateTo]);

  const clearApprovalFilters = () => {
    setApprovalFilterEmployee('');
    setApprovalFilterStatus('');
    setApprovalFilterDateFrom('');
    setApprovalFilterDateTo('');
  };

  const filteredOvertimeRequests = useMemo(() => {
    let list = overtimeRequests || [];
    const emp = (overtimeFilterEmployee || '').trim().toLowerCase();
    if (emp) {
      list = list.filter((r) => {
        const name = r.adminId && typeof r.adminId === 'object'
          ? (r.adminId.username || '').toLowerCase()
          : String(r.adminId || '').toLowerCase();
        return name.includes(emp);
      });
    }
    if (overtimeFilterStatus) {
      list = list.filter((r) => r.status === overtimeFilterStatus);
    }
    if (overtimeFilterDateFrom || overtimeFilterDateTo) {
      const from = overtimeFilterDateFrom ? new Date(overtimeFilterDateFrom) : null;
      const to = overtimeFilterDateTo ? new Date(overtimeFilterDateTo) : null;
      if (from || to) {
        list = list.filter((r) => {
          const raw = r.date || r.createdAt;
          const reqDate = raw ? new Date(raw) : null;
          if (!reqDate || Number.isNaN(reqDate.getTime())) return false;
          if (from && reqDate < from) return false;
          if (to && reqDate > to) return false;
          return true;
        });
      }
    }
    return list;
  }, [overtimeRequests, overtimeFilterEmployee, overtimeFilterStatus, overtimeFilterDateFrom, overtimeFilterDateTo]);

  const clearOvertimeFilters = () => {
    setOvertimeFilterEmployee('');
    setOvertimeFilterStatus('');
    setOvertimeFilterDateFrom('');
    setOvertimeFilterDateTo('');
  };

  const fetchPublicHolidays = () => {
    setLoadingPublicHolidays(true);
    hrApi.get('/api/public-holidays', { params: { year: publicHolidayYear } })
      .then((res) => setPublicHolidays(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPublicHolidays([]))
      .finally(() => setLoadingPublicHolidays(false));
  };

  useEffect(() => {
    if (viewMode === VIEW_MANAGER && showPublicHolidayManageModal) fetchPublicHolidays();
  }, [viewMode, showPublicHolidayManageModal, publicHolidayYear]);

  const fetchAllowanceList = () => {
    setLoadingAllowanceList(true);
    hrApi.get('/api/holidays/allowance/list', { params: { year: allowanceListYear } })
      .then((res) => setAllowanceList(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAllowanceList([]))
      .finally(() => setLoadingAllowanceList(false));
  };

  useEffect(() => {
    if (viewMode === VIEW_MANAGER && (teamTab === 'holiday-approvals' || teamTab === 'holiday')) fetchAllowanceList();
  }, [viewMode, teamTab, allowanceListYear]);

  useEffect(() => {
    if (viewMode === VIEW_MANAGER && teamTab === 'holiday') {
      hrApi.get('/api/leave-types').then((res) => setLeaveTypes(Array.isArray(res.data) ? res.data : [])).catch(() => setLeaveTypes([]));
    }
  }, [viewMode, teamTab]);

  const openEmployeeAllowanceModal = (employeeId) => {
    const id = (employeeId != null ? String(employeeId).trim() : '') || (allowanceEmployeeId && String(allowanceEmployeeId).trim());
    if (!id) return;
    setError('');
    setEditingAllowanceEmployeeId(id);
    const year = new Date().getFullYear();
    setEmployeeAllowanceForm({ year, allowances: [] });
    setEmployeeAllowanceData(null);
    setShowEmployeeAllowanceModal(true);
    setLoadingEmployeeAllowance(true);
    hrApi.get('/api/holidays/allowance', { params: { adminId: id, year } })
      .then((res) => {
        const d = res.data;
        setEmployeeAllowanceForm({ year: d?.year ?? year, allowances: Array.isArray(d?.allowances) ? d.allowances : [], totalOver: d?.totalOver ?? 0 });
        setEmployeeAllowanceData(d);
      })
      .catch(() => setEmployeeAllowanceData(null))
      .finally(() => setLoadingEmployeeAllowance(false));
  };

  const fetchEmployeeAllowanceForModal = (adminId, year) => {
    if (!adminId || !year) return;
    setLoadingEmployeeAllowance(true);
    hrApi.get('/api/holidays/allowance', { params: { adminId, year } })
      .then((res) => {
        const d = res.data;
        setEmployeeAllowanceForm((f) => ({ ...f, year: d?.year ?? year, allowances: Array.isArray(d?.allowances) ? d.allowances : [], totalOver: d?.totalOver ?? 0 }));
        setEmployeeAllowanceData(d);
      })
      .catch(() => setEmployeeAllowanceData(null))
      .finally(() => setLoadingEmployeeAllowance(false));
  };

  const handleEmployeeAllowanceYearChange = (e) => {
    const y = Number(e.target.value);
    setEmployeeAllowanceForm((f) => ({ ...f, year: y }));
    if (editingAllowanceEmployeeId) fetchEmployeeAllowanceForModal(editingAllowanceEmployeeId, y);
  };

  const handleEmployeeAllowanceDaysChange = (leaveTypeCode, value) => {
    setEmployeeAllowanceForm((f) => ({
      ...f,
      allowances: f.allowances.map((a) => (a.leaveTypeCode === leaveTypeCode ? { ...a, allowanceDays: value } : a)),
    }));
  };

  const handleSaveEmployeeAllowance = (e) => {
    e.preventDefault();
    const employeeId = editingAllowanceEmployeeId && String(editingAllowanceEmployeeId).trim();
    if (!employeeId) return;
    const normalizeCode = (v) => (v != null ? String(v).trim().toLowerCase().replace(/\s+/g, '-') : '') || 'annual';
    const allowances = (employeeAllowanceForm.allowances || []).map((a) => ({
      leaveTypeCode: normalizeCode(a.leaveTypeCode || a.code),
      allowanceDays: Math.min(366, Math.max(0, parseInt(a.allowanceDays, 10) || 0)),
    }));
    setError('');
    setSavingEmployeeAllowance(true);
    hrApi.put('/api/holidays/allowance', {
      adminId: employeeId,
      year: employeeAllowanceForm.year,
      allowances,
    })
      .then(() => {
        fetchEmployeeAllowanceForModal(employeeId, employeeAllowanceForm.year);
        fetchAllowanceList();
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to save'))
      .finally(() => setSavingEmployeeAllowance(false));
  };

  const openDefaultAllowanceModal = () => {
    setShowDefaultAllowanceModal(true);
    setLoadingDefaultAllowance(true);
    const y = defaultAllowanceForm.year || new Date().getFullYear();
    hrApi.get('/api/holidays/default-allowance', { params: { year: y } })
      .then((res) => setDefaultAllowanceForm({ year: res.data.year, allowances: Array.isArray(res.data.allowances) ? res.data.allowances : [] }))
      .catch(() => setDefaultAllowanceForm((f) => ({ ...f, year: y, allowances: [] })))
      .finally(() => setLoadingDefaultAllowance(false));
  };

  const handleDefaultAllowanceYearChange = (y) => {
    setDefaultAllowanceForm((f) => ({ ...f, year: y }));
    setLoadingDefaultAllowance(true);
    hrApi.get('/api/holidays/default-allowance', { params: { year: y } })
      .then((res) => setDefaultAllowanceForm({ year: res.data.year, allowances: Array.isArray(res.data.allowances) ? res.data.allowances : [] }))
      .catch(() => setDefaultAllowanceForm((f) => ({ ...f, year: y, allowances: [] })))
      .finally(() => setLoadingDefaultAllowance(false));
  };

  const handleDefaultAllowanceDaysChange = (leaveTypeCode, value) => {
    setDefaultAllowanceForm((f) => ({
      ...f,
      allowances: f.allowances.map((a) => (a.leaveTypeCode === leaveTypeCode ? { ...a, allowanceDays: value } : a)),
    }));
  };

  const handleSaveDefaultAllowance = (e) => {
    e.preventDefault();
    const year = defaultAllowanceForm.year || new Date().getFullYear();
    const allowances = (defaultAllowanceForm.allowances || []).map((a) => ({
      leaveTypeCode: a.leaveTypeCode || a.code,
      allowanceDays: Math.min(366, Math.max(0, parseInt(a.allowanceDays, 10) || 0)),
    }));
    setError('');
    setSavingDefaultAllowance(true);
    hrApi.put('/api/holidays/default-allowance', { year, allowances })
      .then(() => {
        setShowDefaultAllowanceModal(false);
        fetchAllowanceList();
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to save'))
      .finally(() => setSavingDefaultAllowance(false));
  };

  const openAddPublicHoliday = () => {
    const y = publicHolidayYear;
    setPublicHolidayForm({ name: '', nameFr: '', date: `${y}-01-01`, endDate: '' });
    setPublicHolidayModal({ type: 'add' });
  };

  const openEditPublicHoliday = (h) => {
    const d = h.date ? (typeof h.date === 'string' ? h.date.slice(0, 10) : new Date(h.date).toISOString().slice(0, 10)) : '';
    const ed = h.endDate ? (typeof h.endDate === 'string' ? h.endDate.slice(0, 10) : new Date(h.endDate).toISOString().slice(0, 10)) : '';
    setPublicHolidayForm({ name: h.name || '', nameFr: h.nameFr || '', date: d, endDate: ed });
    setPublicHolidayModal({ type: 'edit', id: h._id });
  };

  const savePublicHoliday = () => {
    const { name, nameFr, date, endDate } = publicHolidayForm;
    if (!name.trim()) return;
    setSavingPublicHoliday(true);
    const payload = { name: name.trim(), nameFr: nameFr.trim(), date };
    if (endDate && endDate.trim()) payload.endDate = endDate.trim();
    if (publicHolidayModal.type === 'add') {
      hrApi.post('/api/public-holidays', payload)
        .then(() => { fetchPublicHolidays(); setPublicHolidayModal(null); setPublicHolidaysRefreshKey((k) => k + 1); })
        .catch((err) => setError(err?.response?.data?.message || 'Failed to add'))
        .finally(() => setSavingPublicHoliday(false));
    } else {
      hrApi.put(`/api/public-holidays/${publicHolidayModal.id}`, payload)
        .then(() => { fetchPublicHolidays(); setPublicHolidayModal(null); setPublicHolidaysRefreshKey((k) => k + 1); })
        .catch((err) => setError(err?.response?.data?.message || 'Failed to update'))
        .finally(() => setSavingPublicHoliday(false));
    }
  };

  const deletePublicHoliday = async (id) => {
    if (!window.confirm('Remove this public holiday?')) return;
    try {
      await hrApi.delete(`/api/public-holidays/${id}`);
      fetchPublicHolidays();
      setPublicHolidaysRefreshKey((k) => k + 1);
    } catch (_) {}
  };

  const handleHolidaySubmit = (payload) => {
    setHolidayError('');
    setHolidaySuccessMessage('');
    setHolidayLoading(true);
    const hasFiles = payload.files && payload.files.length > 0;
    const body = { startDate: payload.startDate, endDate: payload.endDate, comment: payload.comment || '', leaveTypeCode: payload.leaveTypeCode || 'annual' };
    if (payload.halfDay === 'am' || payload.halfDay === 'pm') body.halfDay = payload.halfDay;
    const req = hasFiles
      ? (() => {
          const formData = new FormData();
          formData.append('startDate', payload.startDate);
          formData.append('endDate', payload.endDate);
          formData.append('comment', payload.comment || '');
          formData.append('leaveTypeCode', payload.leaveTypeCode || 'annual');
          if (payload.halfDay === 'am' || payload.halfDay === 'pm') formData.append('halfDay', payload.halfDay);
          payload.files.forEach((f) => formData.append('attachments', f));
          return hrApi.post('/api/holidays', formData);
        })()
      : hrApi.post('/api/holidays', body);
    req
      .then(() => {
        fetchMyHolidays();
        setHolidaySuccessMessage('Your leave request was submitted.');
      })
      .catch((err) => setHolidayError(err?.response?.data?.message || 'Failed to submit'))
      .finally(() => setHolidayLoading(false));
  };

  const handleOvertimeSubmit = (payload) => {
    setOvertimeEmployeeError('');
    setOvertimeEmployeeSuccessMessage('');
    setOvertimeEmployeeLoading(true);
    hrApi.post('/api/overtime', payload)
      .then(() => {
        fetchMyOvertime();
        setOvertimeEmployeeSuccessMessage('Your overtime request was submitted.');
      })
      .catch((err) => setOvertimeEmployeeError(err?.response?.data?.message || 'Failed to submit'))
      .finally(() => setOvertimeEmployeeLoading(false));
  };

  const performHolidayDecision = async (holidayId, action, rejectReason, actedOnBehalfOf) => {
    const payload = action === 'approve' ? { status: 'approved' } : { status: 'rejected', rejectedReason: rejectReason };
    if (actedOnBehalfOf) payload.actedOnBehalfOf = actedOnBehalfOf;
    try {
      await hrApi.put(`/api/holidays/${holidayId}`, payload);
      setHolidayRejectModal(null);
      setHolidayEditModal(null);
      setHolidayRejectReason('');
      setActAsModal({ show: false, action: null, holidayId: null, rejectReason: null, approvedDays: undefined });
      closeHolidayViewModal();
      setHolidayApprovalsRefreshKey((k) => k + 1);
    } catch (_) {}
  };

  const handleApproveHoliday = (id, currentStatus) => {
    if (currentStatus === 'approved') {
      window.alert('This request is already approved.');
      return;
    }
    const isAssistantManager = me?.role === 'assistant_manager';
    if (isAssistantManager) {
      setActAsModal({ show: true, action: 'approve', holidayId: id, rejectReason: null, approvedDays: undefined });
    } else {
      performHolidayDecision(id, 'approve', null, null);
    }
  };

  const handleRejectHoliday = () => {
    const id = holidayRejectModal?.id || holidayEditModal?.id;
    const currentStatus = holidayRejectModal?.status ?? holidayEditModal?.status;
    const reason = (holidayRejectReason || '').trim();
    if (!id || !reason) return;
    if (currentStatus === 'rejected') {
      window.alert('This request is already rejected.');
      return;
    }
    const isAssistantManager = me?.role === 'assistant_manager';
    if (isAssistantManager) {
      setActAsModal({ show: true, action: 'reject', holidayId: id, rejectReason: reason, approvedDays: undefined });
    } else {
      performHolidayDecision(id, 'reject', reason, null);
    }
  };

  const closeHolidayDecisionModals = () => {
    setHolidayRejectModal(null);
    setHolidayEditModal(null);
    setHolidayRejectReason('');
  };

  const handleRejectFromDashboard = (request) => {
    if (!request?._id) return;
    setHolidayRejectModal({ id: request._id, status: request.status || 'pending' });
    setHolidayRejectReason('');
  };

  const applyDashboardPreset = (preset) => {
    const baseNow = new Date();
    const today = new Date(baseNow.getFullYear(), baseNow.getMonth(), baseNow.getDate());
    let start = new Date(today);
    let end = new Date(today);
    if (preset === 'this_week') {
      start = startOfWeekMonday(today);
      end = new Date(start);
      end.setDate(end.getDate() + 6);
    } else if (preset === 'this_month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (preset === 'next_two_weeks') {
      start = startOfWeekMonday(today);
      end = new Date(start);
      end.setDate(end.getDate() + 13);
    }
    setDashboardPreset(preset);
    setDashboardStartDate(toDateInputValue(start));
    setDashboardEndDate(toDateInputValue(end));
  };

  const applyCustomDashboardRange = () => {
    if (!dashboardStartDate || !dashboardEndDate) return;
    const start = new Date(dashboardStartDate + 'T00:00:00');
    const end = new Date(dashboardEndDate + 'T00:00:00');
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      setError('Please select a valid dashboard date range.');
      return;
    }
    setDashboardPreset('custom');
    setError('');
    setHolidayApprovalsRefreshKey((k) => k + 1);
  };

  const closeHolidayViewModal = () => {
    setHolidayViewRequest(null);
    setViewModalRejectReason('');
  };

  // Deep link support: /manager?dl_type=holiday|overtime&dl_id=<id>
  useEffect(() => {
    const dlId = searchParams.get('dl_id');
    if (!dlId) return;

    // Clear params immediately to prevent re-triggering on refresh.
    // Land on the default Dashboard view — no tab change needed.
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleRejectFromViewModal = () => {
    const reason = (viewModalRejectReason || '').trim();
    if (!holidayViewRequest?._id || !reason) return;
    if (holidayViewRequest.status === 'rejected') {
      window.alert('This request is already rejected.');
      return;
    }
    const isAssistantManager = me?.role === 'assistant_manager';
    if (isAssistantManager) {
      setActAsModal({ show: true, action: 'reject', holidayId: holidayViewRequest._id, rejectReason: reason, approvedDays: undefined });
    } else {
      performHolidayDecision(holidayViewRequest._id, 'reject', reason, null);
    }
  };

  const handleApproveFromViewModal = () => {
    if (!holidayViewRequest?._id) return;
    if (holidayViewRequest.status === 'approved') {
      window.alert('This request is already approved.');
      return;
    }
    const isAssistantManager = me?.role === 'assistant_manager';
    if (isAssistantManager) {
      setActAsModal({ show: true, action: 'approve', holidayId: holidayViewRequest._id, rejectReason: null, approvedDays: undefined });
    } else {
      performHolidayDecision(holidayViewRequest._id, 'approve', null, null);
    }
  };

  const performOvertimeDecision = async (requestId, action, rejectReason, actedOnBehalfOf, approvedDays) => {
    const payload = action === 'approve'
      ? { status: 'approved', approvedDays: Number(approvedDays) }
      : { status: 'rejected', rejectedReason: rejectReason };
    if (actedOnBehalfOf) payload.actedOnBehalfOf = actedOnBehalfOf;
    try {
      setOvertimeDecisionLoadingId(requestId);
      setOvertimeDecisionError('');
      setOvertimeDecisionMessage('');
      await hrApi.put(`/api/overtime/${requestId}`, payload);
      setOvertimeDecisionMessage(action === 'approve' ? 'Overtime request approved.' : 'Overtime request rejected.');
      const grantedDays = action === 'approve' && Number.isFinite(Number(approvedDays)) ? Number(approvedDays) : null;
      setOvertimeRequests((prev) => prev.map((r) => (
        r._id === requestId
          ? {
            ...r,
            status: action === 'approve' ? 'approved' : 'rejected',
            rejectedReason: action === 'reject' ? rejectReason : null,
            approvedDays: action === 'approve' ? grantedDays : null,
          }
          : r
      )));
      setOvertimeRejectModal(null);
      setOvertimeRejectReason('');
      setOvertimeApproveModal(null);
      setOvertimeApproveDays('');
      setOvertimeViewRequest(null);
      setActAsModal({ show: false, action: null, holidayId: null, rejectReason: null, approvedDays: undefined });
      setHolidayApprovalsRefreshKey((k) => k + 1);
      setOvertimeEmployeeRequests((prev) => prev.map((r) => (
        r._id === requestId
          ? {
            ...r,
            status: action === 'approve' ? 'approved' : 'rejected',
            rejectedReason: action === 'reject' ? rejectReason : null,
            approvedDays: action === 'approve' ? grantedDays : null,
          }
          : r
      )));
    } catch (err) {
      setOvertimeDecisionError(err?.response?.data?.message || 'Failed to update overtime request');
    } finally {
      setOvertimeDecisionLoadingId(null);
    }
  };

  const handleApproveOvertime = (request) => {
    if (!request?._id || request.status === 'approved') return;
    const employeeName = request.adminId && typeof request.adminId === 'object' ? dn(request.adminId.username) : '';
    setOvertimeViewRequest(null);
    setOvertimeApproveModal({ id: request._id, employeeName });
    setOvertimeApproveDays(request.approvedDays != null && Number.isFinite(Number(request.approvedDays)) ? String(request.approvedDays) : '');
  };

  const confirmOvertimeApproveWithDays = () => {
    if (!overtimeApproveModal?.id) return;
    const n = Number(overtimeApproveDays);
    if (!Number.isFinite(n) || n <= 0) return;
    const id = overtimeApproveModal.id;
    const isAssistantManager = me?.role === 'assistant_manager';
    if (isAssistantManager) {
      setActAsModal({
        show: true, action: 'approve-overtime', holidayId: id, rejectReason: null, approvedDays: n,
      });
    } else {
      performOvertimeDecision(id, 'approve', null, null, n);
    }
    setOvertimeApproveModal(null);
    setOvertimeApproveDays('');
  };

  const handleRejectOvertime = (request) => {
    if (!request?._id || request.status === 'rejected') return;
    setOvertimeViewRequest(null);
    setOvertimeRejectModal({ id: request._id });
    setOvertimeRejectReason('');
  };

  const closeOvertimeViewModal = () => {
    setOvertimeViewRequest(null);
  };

  const handleApproveOvertimeFromView = () => {
    const request = overtimeViewRequest;
    if (!request?._id) return;
    setOvertimeViewRequest(null);
    handleApproveOvertime(request);
  };

  const handleRejectOvertimeFromView = () => {
    const request = overtimeViewRequest;
    if (!request?._id) return;
    setOvertimeViewRequest(null);
    handleRejectOvertime(request);
  };

  const handleReapproveOvertimeFromView = () => {
    if (!overtimeViewRequest?._id || overtimeViewRequest.status !== 'rejected') return;
    const id = overtimeViewRequest._id;
    const employeeName = overtimeViewRequest.adminId && typeof overtimeViewRequest.adminId === 'object'
      ? dn(overtimeViewRequest.adminId.username)
      : '';
    setOvertimeViewRequest(null);
    setOvertimeApproveModal({ id, employeeName });
    setOvertimeApproveDays('');
  };

  const confirmRejectOvertime = () => {
    if (!overtimeRejectModal?.id || !overtimeRejectReason.trim()) return;
    const isAssistantManager = me?.role === 'assistant_manager';
    if (isAssistantManager) {
      const reason = overtimeRejectReason.trim();
      const id = overtimeRejectModal.id;
      setOvertimeRejectModal(null);
      setOvertimeRejectReason('');
      setActAsModal({
        show: true,
        action: 'reject-overtime',
        holidayId: id,
        rejectReason: reason,
        approvedDays: undefined,
      });
    } else {
      performOvertimeDecision(overtimeRejectModal.id, 'reject', overtimeRejectReason.trim(), null, undefined);
    }
  };

  const downloadHolidayAttachment = (requestId, attachment) => {
    hrApi
      .get(`/api/holidays/${requestId}/attachments/${attachment.filename}`, { responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.originalName || attachment.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      })
      .catch(() => {});
  };

  const handleExport = (reportType, format) => {
    if (!exportStartDate || !exportEndDate) {
      setError('Please set start and end date for export.');
      return;
    }
    setExporting(true);
    setError('');
    const params = new URLSearchParams({ startDate: exportStartDate, endDate: exportEndDate });
    if (exportAdminIds.length > 0) params.set('adminIds', exportAdminIds.join(','));
    const endpoint = reportType === 'summary'
      ? (format === 'pdf' ? '/api/working-hours/export-summary-pdf' : '/api/working-hours/export-summary')
      : (format === 'pdf' ? '/api/working-hours/export-pdf' : '/api/working-hours/export');
    const resolvedReportType = format === 'pdf' ? 'report' : reportType;
    const prefix = resolvedReportType === 'summary' ? 'working-hours-summary' : resolvedReportType === 'daily' ? 'working-hours-daily' : 'working-hours-report';
    hrApi.get(`${endpoint}?${params.toString()}`, { responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${prefix}-${exportStartDate}-to-${exportEndDate}.${format}`;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setError('Export failed'))
      .finally(() => setExporting(false));
  };

  const setSummaryRange = (start, end) => {
    setSummaryStartDate(start);
    setSummaryEndDate(end);
  };

  const setExportRange = (start, end) => {
    setExportStartDate(start);
    setExportEndDate(end);
  };

  const toggleExportEmployee = (id) => {
    setExportAdminIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openExportEmployeesModal = () => {
    setExportModalSelectedIds(exportAdminIds.length > 0 ? [...exportAdminIds] : []);
    setShowExportEmployeesModal(true);
  };

  const toggleExportModalEmployee = (id) => {
    setExportModalSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const selectAllExportModal = () => setExportModalSelectedIds(employees.map((e) => e._id));
  const clearExportModal = () => setExportModalSelectedIds([]);
  const saveExportEmployeesModal = () => {
    setExportAdminIds([...exportModalSelectedIds]);
    setShowExportEmployeesModal(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate(isAdminView ? '/admin/login' : '/login', { replace: true });
  };

  const annualAllowanceView = (() => {
    const list = Array.isArray(allowance?.allowances) ? allowance.allowances : [];
    if (!list.length) return null;
    const vacation = list.find((a) => String(a.leaveTypeCode || '').toLowerCase() === 'vacation');
    const carry = list.find((a) => String(a.leaveTypeCode || '').toLowerCase() === 'carry-over');
    if (!vacation && !carry) {
      const first = list[0];
      return {
        allowanceDays: first?.allowanceDays ?? '–',
        usedDays: first?.usedDays ?? '–',
        remaining: first?.remaining ?? '–',
      };
    }
    return {
      allowanceDays: (vacation?.allowanceDays || 0) + (carry?.allowanceDays || 0),
      usedDays: (vacation?.usedDays || 0) + (carry?.usedDays || 0),
      remaining: (vacation?.remaining || 0) + (carry?.remaining || 0),
    };
  })();
  const employeeOverview = useMemo(() => {
    const otBalance = allowance?.overtimeBalance || { total: 0, usedDays: 0, remaining: 0 };
    return {
      approvedOvertimeDays: Math.round(otBalance.remaining * 100) / 100,
      overtimeBalance: otBalance,
    };
  }, [allowance]);

  const overviewRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let requestedStart;
    let requestedEnd;
    let label;
    if (overviewRangePreset === 'this_month') {
      requestedStart = new Date(today.getFullYear(), today.getMonth(), 1);
      requestedEnd = endOfMonth(today);
      label = 'This month';
    } else if (overviewRangePreset === 'next_two_weeks') {
      requestedStart = new Date(today);
      requestedEnd = new Date(today);
      requestedEnd.setDate(requestedEnd.getDate() + 13);
      requestedEnd.setHours(23, 59, 59, 999);
      label = 'Next two weeks';
    } else {
      requestedStart = startOfWeekMonday(today);
      requestedEnd = new Date(requestedStart);
      requestedEnd.setDate(requestedEnd.getDate() + 6);
      requestedEnd.setHours(23, 59, 59, 999);
      label = 'This week';
    }
    const start = startOfWeekMonday(requestedStart);
    const end = endOfWeekSunday(requestedEnd);
    return { start, end, requestedStart, requestedEnd, label };
  }, [overviewRangePreset]);

  useEffect(() => {
    const startYear = overviewRange.start.getFullYear();
    const endYear = overviewRange.end.getFullYear();
    const years = startYear === endYear ? [startYear] : [startYear, endYear];
    Promise.all(
      years.map((year) =>
        hrApi.get('/api/public-holidays', { params: { year } }).then((res) => (Array.isArray(res.data) ? res.data : []))
      )
    )
      .then((lists) => setOverviewPublicHolidays(lists.flat()))
      .catch(() => setOverviewPublicHolidays([]));
  }, [overviewRange.start, overviewRange.end]);

  const overviewRangeDays = useMemo(() => {
    const approvedLeaveByDate = {};
    holidayRequests
      .filter((r) => String(r?.status || '').toLowerCase() === 'approved')
      .forEach((r) => {
        const start = new Date(r.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(r.endDate);
        end.setHours(0, 0, 0, 0);
        const d = new Date(start);
        while (d <= end) {
          const key = toDateInputValue(d);
          if (!approvedLeaveByDate[key]) approvedLeaveByDate[key] = [];
          approvedLeaveByDate[key].push(r);
          d.setDate(d.getDate() + 1);
        }
      });

    const workingByDate = {};
    overviewWorkingEntries.forEach((entry) => {
      const key = toDateInputValue(new Date(entry.date));
      workingByDate[key] = entry;
    });

    const workingDays = Array.isArray(overviewWorkSettings?.workingDays) && overviewWorkSettings.workingDays.length > 0
      ? overviewWorkSettings.workingDays
      : [1, 2, 3, 4, 5];
    const daySchedule = overviewWorkSettings?.daySchedule && typeof overviewWorkSettings.daySchedule === 'object'
      ? overviewWorkSettings.daySchedule
      : {};

    const publicHolidayByDate = {};
    overviewPublicHolidays.forEach((ph) => {
      if (!ph?.date) return;
      const start = new Date(ph.date);
      start.setHours(0, 0, 0, 0);
      const end = ph.endDate ? new Date(ph.endDate) : new Date(ph.date);
      end.setHours(0, 0, 0, 0);
      const d = new Date(start);
      while (d <= end) {
        const key = toDateInputValue(d);
        publicHolidayByDate[key] = ph.name || ph.nameFr || 'Public holiday';
        d.setDate(d.getDate() + 1);
      }
    });

    const days = [];
    const cursor = new Date(overviewRange.start);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(overviewRange.end);
    end.setHours(0, 0, 0, 0);
    while (cursor <= end) {
      const key = toDateInputValue(cursor);
      const weekday = cursor.getDay();
      const isWeekend = weekday === 0 || weekday === 6;
      const isOffDay = !isWeekend && (!workingDays.includes(weekday) || daySchedule[String(weekday)] === 'off');
      const publicHolidayName = publicHolidayByDate[key] || null;
      const leaveCountRaw = (approvedLeaveByDate[key] || []).length;
      const inRange = cursor >= overviewRange.requestedStart && cursor <= overviewRange.requestedEnd;
      days.push({
        key,
        label: cursor.toLocaleDateString(undefined, { weekday: 'short' }),
        dateText: formatDateShort(cursor),
        inRange,
        isToday: key === toDateInputValue(new Date()),
        isWeekend,
        isOffDay,
        publicHolidayName,
        leaveCount: publicHolidayName ? 0 : leaveCountRaw,
        entry: workingByDate[key] || null,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [holidayRequests, overviewPublicHolidays, overviewRange.end, overviewRange.start, overviewWorkSettings, overviewWorkingEntries]);

  const pendingOverviewItems = useMemo(() => {
    const leaveItems = holidayRequests
      .filter((r) => String(r?.status || '').toLowerCase() === 'pending')
      .map((r) => {
        const s = new Date(r.startDate);
        const e = new Date(r.endDate);
        const same = toDateInputValue(s) === toDateInputValue(e);
        const dateLabel = same
          ? s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
        return {
          id: `leave-${r._id}`,
          kind: 'leave',
          dateLabel,
          createdAt: r.createdAt || r.updatedAt || r.startDate,
        };
      });
    const overtimeItems = overtimeEmployeeRequests
      .filter((r) => String(r?.status || '').toLowerCase() === 'pending')
      .map((r) => {
        const applied = r.createdAt ? new Date(r.createdAt) : new Date();
        const dLabel = applied.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        return {
          id: `ot-${r._id}`,
          kind: 'overtime',
          dateLabel: `Applied ${dLabel}`,
          createdAt: r.createdAt || r.updatedAt,
        };
      });
    return [...leaveItems, ...overtimeItems]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 8);
  }, [holidayRequests, overtimeEmployeeRequests]);

  return (
    <div className="employee-dashboard">
      <nav className="hr-sidebar">
        <div className="hr-sidebar-logo">
          <span className="hr-sidebar-logo-mark" aria-label="Staff Portal">SP</span>
          <span className="hr-sidebar-logo-text">Staff Portal</span>
        </div>
        <div className="hr-sidebar-view-toggle">
          <button
            type="button"
            className={`btn btn-sm w-50 ${viewMode === VIEW_MANAGER ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode(VIEW_MANAGER)}
          >
            <i className="bi bi-people me-1" /> Team
          </button>
          <button
            type="button"
            className={`btn btn-sm w-50 ${viewMode === VIEW_EMPLOYEE ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode(VIEW_EMPLOYEE)}
          >
            <i className="bi bi-person me-1" /> Me
          </button>
        </div>
        {viewMode === VIEW_MANAGER ? (
          <ul className="nav flex-column">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${teamTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setTeamTab('dashboard')}
              >
                <i className="bi bi-speedometer2" /> Dashboard
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${teamTab === 'calendar' ? 'active' : ''}`}
                onClick={() => setTeamTab('calendar')}
              >
                <i className="bi bi-calendar3" /> Team calendar
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${teamTab === 'summary-export' ? 'active' : ''}`}
                onClick={() => setTeamTab('summary-export')}
              >
                <i className="bi bi-table" /> Summary & export
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${teamTab === 'holiday' ? 'active' : ''}`}
                onClick={() => setTeamTab('holiday')}
              >
                <i className="bi bi-calendar-event" /> Holiday calendar
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${teamTab === 'holiday-approvals' ? 'active' : ''}`}
                onClick={() => setTeamTab('holiday-approvals')}
              >
                <i className="bi bi-calendar-check" /> Holiday approvals
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${teamTab === 'overtime-approvals' ? 'active' : ''}`}
                onClick={() => setTeamTab('overtime-approvals')}
              >
                <i className="bi bi-clock-history" /> Overtime approvals
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${teamTab === 'activity' ? 'active' : ''}`}
                onClick={() => setTeamTab('activity')}
              >
                <i className="bi bi-journal-text" /> Audit trail
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${teamTab === 'settings' ? 'active' : ''}`}
                onClick={() => setTeamTab('settings')}
              >
                <i className="bi bi-gear" /> Settings
              </button>
            </li>
          </ul>
        ) : (
          <ul className="nav flex-column">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${activeSection === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveSection('overview')}
              >
                <i className="bi bi-speedometer2" /> Overview
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${activeSection === 'working-hours' ? 'active' : ''}`}
                onClick={() => setActiveSection('working-hours')}
              >
                <i className="bi bi-calendar3" /> Working hours
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${activeSection === 'holidays' ? 'active' : ''}`}
                onClick={() => setActiveSection('holidays')}
              >
                <i className="bi bi-calendar-check" /> Holiday requests
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link w-100 text-start border-0 ${activeSection === 'overtime' ? 'active' : ''}`}
                onClick={() => setActiveSection('overtime')}
              >
                <i className="bi bi-clock-history" /> Overtime requests
              </button>
            </li>
          </ul>
        )}
      </nav>

      <main className="hr-main">
        <div className="hr-main-topbar d-flex align-items-center justify-content-between px-3 py-2">
          <span className="hr-view-label">
            {dn(me?.username) || 'User'} ({isAdminView ? 'Admin' : me?.role === 'assistant_manager' ? 'Assistant Manager' : viewMode === VIEW_MANAGER ? 'Manager' : 'Employee'})
          </span>
          <div className="d-flex gap-2">
            {isAdminView && onSwitchToAdmin && (
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={onSwitchToAdmin}>
                <i className="bi bi-shield-lock me-1" /> Admin panel
              </button>
            )}
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right me-1" /> Logout
            </button>
          </div>
        </div>
        <div className="hr-outlet">
          {viewMode === VIEW_MANAGER ? (
            <>
              {actAsModal.show && actAsModal.holidayId && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {String(actAsModal.action || '').startsWith('approve') ? 'Approve request' : 'Reject request'} – perform as
                        </h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => setActAsModal({ show: false, action: null, holidayId: null, rejectReason: null, approvedDays: undefined })} />
                      </div>
                      <div className="modal-body">
                        <p className="small text-muted mb-3">
                          {String(actAsModal.action || '').startsWith('approve')
                            ? 'Choose whether this approval is recorded as your own or on behalf of your manager.'
                            : 'Choose whether this rejection is recorded as your own or on behalf of your manager.'}
                        </p>
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                              if (actAsModal.action === 'approve-overtime' || actAsModal.action === 'reject-overtime') {
                                performOvertimeDecision(
                                  actAsModal.holidayId,
                                  actAsModal.action === 'approve-overtime' ? 'approve' : 'reject',
                                  actAsModal.rejectReason,
                                  null,
                                  actAsModal.action === 'approve-overtime' ? actAsModal.approvedDays : undefined
                                );
                              } else {
                                performHolidayDecision(actAsModal.holidayId, actAsModal.action, actAsModal.rejectReason, null);
                              }
                            }}
                          >
                            As yourself
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              const onBehalfId = me?.assistantManager?.primaryManagerId != null
                                ? String(me.assistantManager.primaryManagerId)
                                : null;
                              if (actAsModal.action === 'approve-overtime' || actAsModal.action === 'reject-overtime') {
                                performOvertimeDecision(
                                  actAsModal.holidayId,
                                  actAsModal.action === 'approve-overtime' ? 'approve' : 'reject',
                                  actAsModal.rejectReason,
                                  onBehalfId,
                                  actAsModal.action === 'approve-overtime' ? actAsModal.approvedDays : undefined
                                );
                              } else {
                                performHolidayDecision(actAsModal.holidayId, actAsModal.action, actAsModal.rejectReason, onBehalfId);
                              }
                            }}
                          >
                            On behalf of {me?.assistantManager?.primaryManagerName || 'manager'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="hr-page-header d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 w-100">
                {teamTab === 'dashboard' ? (
                  <>
                    <h2 className="hr-page-title mb-0 flex-grow-1" style={{ fontSize: '1.65rem' }}>
                      {(() => {
                        const swissHour = parseInt(new Date().toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: 'Europe/Zurich' }), 10);
                        const timeGreeting = swissHour < 12 ? 'Good morning' : swissHour < 17 ? 'Good afternoon' : 'Good evening';
                        const name = dn(me?.username || me?.email?.split('@')[0] || 'there');
                        return `${timeGreeting}, ${name}`;
                      })()}
                    </h2>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setHolidayApprovalsRefreshKey((k) => k + 1)}
                    >
                      <i className="bi bi-arrow-clockwise" /> Refresh
                    </button>
                  </>
                ) : (
                  <h2 className="hr-page-title mb-0 flex-grow-1">
                    {teamTab === 'calendar' ? 'Team calendar' : teamTab === 'holiday' ? 'Holiday calendar' : teamTab === 'holiday-approvals' ? 'Holiday approvals' : teamTab === 'overtime-approvals' ? 'Overtime approvals' : teamTab === 'activity' ? 'Audit trail' : teamTab === 'settings' ? 'Settings' : 'Summary & export'}
                  </h2>
                )}
                {teamTab === 'holiday' && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={openDefaultAllowanceModal}
                    >
                      <i className="bi bi-gear" /> Default allowance
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setShowLeaveTypesModal(true);
                        hrApi.get('/api/leave-types').then((res) => setLeaveTypes(Array.isArray(res.data) ? res.data : [])).catch(() => setLeaveTypes([]));
                      }}
                    >
                      <i className="bi bi-tags" /> Manage leave types
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowPublicHolidayManageModal(true)}
                    >
                      <i className="bi bi-calendar-event" /> Manage public holidays
                    </button>
                  </>
                )}
              </div>

              {error && (
                <div className="alert alert-danger py-2 mb-3">{error}</div>
              )}

              {teamTab === 'dashboard' && (
                <>
                  {managerDashboardError && (
                    <div className="alert alert-warning py-2 mb-3">{managerDashboardError}</div>
                  )}

                  {/* 2. KPI cards — always about today, never hidden */}
                  {(() => {
                    const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                    const todayData = (patchedWeeklyAvailability || []).find((d) => d.date === todayStr && d.isWorkingDay);
                    const presentToday = todayData ? todayData.presentCount : 0;
                    const absentToday = todayData ? todayData.absentCount : 0;
                    const absentTodayNames = todayData ? (todayData.absentNames || []) : [];
                    const totalTeam = presentToday + absentToday;
                    const kh = managerDashboardData.kpis?.pendingHolidayCount ?? 0;
                    const ko = managerDashboardData.kpis?.pendingOvertimeCount ?? 0;
                    const pendingCount = managerDashboardData.kpis?.pendingRequests != null
                      ? managerDashboardData.kpis.pendingRequests
                      : (managerDashboardData.pendingApprovals || []).length;
                    return (
                      <div className="db-kpis">
                        <div className="db-kpi">
                          <div className="db-kpi-icon db-kpi-icon--team"><i className="bi bi-people-fill" /></div>
                          <div className="db-kpi-body">
                            <div className="db-kpi-val">{totalTeam}</div>
                            <div className="db-kpi-label">Team members</div>
                          </div>
                        </div>
                        <div className="db-kpi">
                          <div className="db-kpi-icon db-kpi-icon--present"><i className="bi bi-person-check-fill" /></div>
                          <div className="db-kpi-body">
                            <div className="db-kpi-val">{presentToday}</div>
                            <div className="db-kpi-label">Present today</div>
                          </div>
                        </div>
                        <div className="db-kpi">
                          <div className="db-kpi-icon db-kpi-icon--absent"><i className="bi bi-person-dash-fill" /></div>
                          <div className="db-kpi-body">
                            <div className="db-kpi-val">{absentToday}</div>
                            <div className="db-kpi-label">Absent today</div>
                            {absentTodayNames.length > 0 && (
                              <div className="db-kpi-sub">{absentTodayNames.map((n) => n.split(' ')[0]).join(', ')}</div>
                            )}
                          </div>
                        </div>
                        <div
                          className="db-kpi db-kpi--clickable"
                          onClick={() => setTeamTab(ko > 0 && kh === 0 ? 'overtime-approvals' : 'holiday-approvals')}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="db-kpi-icon db-kpi-icon--pending"><i className="bi bi-hourglass-split" /></div>
                          <div className="db-kpi-body">
                            <div className="db-kpi-val">{pendingCount}</div>
                            <div className="db-kpi-label">Pending approvals</div>
                          </div>
                          <i className="bi bi-chevron-right db-kpi-arrow" />
                        </div>
                      </div>
                    );
                  })()}

                  <div className="db-pending-section">
                    <div className="db-pending db-pending--below">
                      <div className="db-pending-head">
                        <span className="db-pending-title">Pending requests</span>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <button type="button" className="db-pending-link border-0 bg-transparent p-0" onClick={() => setTeamTab('holiday-approvals')}>Holiday approvals</button>
                          <span className="text-muted small">·</span>
                          <button type="button" className="db-pending-link border-0 bg-transparent p-0" onClick={() => setTeamTab('overtime-approvals')}>Overtime approvals</button>
                        </div>
                      </div>
                      {(managerDashboardData.pendingApprovals || []).length === 0 ? (
                        <p className="db-pending-empty">No pending requests</p>
                      ) : (
                        <div className="db-pending-list">
                          {(managerDashboardData.pendingApprovals || []).map((r) => {
                            const isOvertime = r.kind === 'overtime';
                            const name = r.adminId && typeof r.adminId === 'object' ? dn(r.adminId.username || r.adminId.email) : 'Unknown';
                            let dateLabel;
                            if (isOvertime) {
                              const applied = r.createdAt ? new Date(r.createdAt) : null;
                              const dStr = applied && !Number.isNaN(applied.getTime())
                                ? applied.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                                : '–';
                              dateLabel = `Applied ${dStr}`;
                            } else {
                              const s = new Date(r.startDate);
                              const e = new Date(r.endDate);
                              const same = s.getTime() === e.getTime();
                              dateLabel = same
                                ? s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                                : `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                            }
                            return (
                              <div
                                key={`${r.kind || 'leave'}-${r._id}`}
                                className="db-pending-item"
                                onClick={() => setTeamTab(isOvertime ? 'overtime-approvals' : 'holiday-approvals')}
                                role="button"
                                tabIndex={0}
                              >
                                <div className="db-pending-item-main">
                                  <div className="db-pending-item-meta">
                                    <span className="db-pending-item-name">{name.split(' ')[0]}</span>
                                    <span className={`badge rounded-pill ${isOvertime ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                      {isOvertime ? 'Overtime' : 'Leave'}
                                    </span>
                                  </div>
                                  <span className="db-pending-item-date">{dateLabel}</span>
                                </div>
                                <i className="bi bi-chevron-right db-pending-item-arrow" />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Team overview — calendar + range (below pending) */}
                  <section className="db-team-overview-section">
                    <h2 className="db-section-title">Team overview</h2>
                    <div className="db-range-bar">
                      <div className="btn-group btn-group-sm" role="group">
                        <button type="button" className={`btn ${dashboardPreset === 'this_week' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => applyDashboardPreset('this_week')}>This week</button>
                        <button type="button" className={`btn ${dashboardPreset === 'this_month' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => applyDashboardPreset('this_month')}>This month</button>
                        <button type="button" className={`btn ${dashboardPreset === 'next_two_weeks' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => applyDashboardPreset('next_two_weeks')}>Next 2 weeks</button>
                      </div>
                      <div className="db-range-custom">
                        <input type="date" className="form-control form-control-sm" value={dashboardStartDate} onChange={(e) => setDashboardStartDate(e.target.value)} />
                        <span className="db-range-sep">–</span>
                        <input type="date" className="form-control form-control-sm" value={dashboardEndDate} onChange={(e) => setDashboardEndDate(e.target.value)} />
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={applyCustomDashboardRange}>Apply</button>
                      </div>
                    </div>

                    {managerDashboardLoading && !managerDashboardData.weeklyAvailability?.length ? (
                      <div className="hr-dash-loading">
                        <p className="small text-muted mb-0">Loading...</p>
                      </div>
                    ) : (
                      <div className={`db-main-cal-full ${managerDashboardRefreshing ? 'db-main-cal-full--refreshing' : ''}`}>
                        <DashboardAttendanceRoster
                          days={patchedWeeklyAvailability}
                        />
                      </div>
                    )}
                  </section>
                </>
              )}

              {teamTab === 'settings' && (
                <div className="hr-section-card">
                  <h5 className="mb-2">Add team member</h5>
                  <p className="small text-muted mb-3">Create a new staff account. The new user will be able to log in and use the employee dashboard.</p>
                  <div className="mb-4">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => { setShowAddUserModal(true); setAddUserError(''); setAddUserForm({ username: '', email: '', password: '', role: 'employee' }); }}
                    >
                      <i className="bi bi-person-plus me-1" /> Add user
                    </button>
                  </div>

                  <hr className="my-4" />
                  <h5 className="mb-3">Organization defaults</h5>
                  <p className="small text-muted mb-3">Used for prefill and new entries. Applies to all employees unless overridden below.</p>
                  <form onSubmit={handleSaveDefaults} className="mb-4">
                    <div className="row g-3 align-items-end">
                      <div className="col-auto">
                        <label className="form-label small mb-0">Start time</label>
                        <input type="time" className="form-control form-control-sm" value={defaultForm.defaultStartTime} onChange={handleDefaultStartChange} />
                      </div>
                      <div className="col-auto">
                        <label className="form-label small mb-0">End time</label>
                        <input type="time" className="form-control form-control-sm" value={defaultForm.defaultEndTime} onChange={handleDefaultEndChange} />
                      </div>
                      <div className="col-auto">
                        <label className="form-label small mb-0">Total hours</label>
                        <input type="number" className="form-control form-control-sm" min="0" max="24" step="any" style={{ width: 72 }} value={defaultForm.defaultTotalHours} onChange={handleDefaultTotalChange} />
                      </div>
                      <div className="col-auto">
                        <label className="form-label small mb-0">Break start</label>
                        <input type="time" className="form-control form-control-sm" style={{ width: 90 }} value={defaultForm.breakStartTime} onChange={(e) => setDefaultForm((f) => ({ ...f, breakStartTime: e.target.value }))} />
                      </div>
                      <div className="col-auto">
                        <label className="form-label small mb-0">Break end</label>
                        <input type="time" className="form-control form-control-sm" style={{ width: 90 }} value={defaultForm.breakEndTime} onChange={(e) => setDefaultForm((f) => ({ ...f, breakEndTime: e.target.value }))} />
                      </div>
                      <div className="col-auto align-self-end">
                        <button type="submit" className="btn btn-primary btn-sm" disabled={savingDefaults}>{savingDefaults ? 'Saving...' : 'Save'}</button>
                      </div>
                    </div>
                  </form>

                  <hr className="my-4" />
                  <h5 className="mb-2">Employee overrides</h5>
                  <p className="small text-muted mb-3">Override for a specific employee. Leave empty to use organization defaults.</p>
                  {employees.length === 0 ? (
                    <p className="small text-muted">No employees.</p>
                  ) : (
                    <div className="hr-settings-employee-list">
                      {employees.filter((e) => (e.role_name || '').trim() !== 'SuperAdmin').map((emp) => (
                        <div key={emp._id} className="hr-settings-employee-row">
                          <span className="hr-settings-employee-name">{dn(emp.username || emp.email || emp._id)}</span>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => openEmployeeDefaultsModal(emp._id)}
                          >
                            Override
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <WorkScheduleOverrides employees={employees.filter((e) => String(e.role || '').trim() !== 'manager')} />

                  {me?.role === 'manager' && (
                    <>
                      <hr className="my-4" />
                      <h5 className="mb-2">Assistant Manager</h5>
                      <p className="small text-muted mb-3">Assign staff as Assistant Managers and grant permissions. Granting &quot;Approve/deny holiday&quot; lets them handle leave decisions on your behalf; they can choose to record each decision as themselves or on behalf of you.</p>
                      {loadingAssistants ? (
                        <p className="small text-muted">Loading...</p>
                      ) : (
                        <>
                          {assistantManagers.length > 0 && (
                            <div className="mb-4">
                              <span className="small fw-medium d-block mb-2">Current assistants</span>
                              <ul className="list-unstyled mb-0">
                                {assistantManagers.map((a) => (
                                  <li key={a.assistantId} className="d-flex flex-wrap align-items-center gap-2 py-2 border-bottom">
                                    <span className="fw-medium">{dn(a.username || a.email)}</span>
                                    <span className="small text-muted">{a.permissions?.includes('approve_holiday') ? 'Approve/deny holiday' : '—'}</span>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm ms-auto"
                                      onClick={async () => {
                                        try {
                                          await hrApi.delete(`/api/managers/me/assistants/${a.assistantId}`);
                                          setAssistantManagers((prev) => prev.filter((x) => x.assistantId !== a.assistantId));
                                        } catch (_) {}
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className="border rounded p-3 bg-light">
                            <span className="small fw-medium d-block mb-2">Add assistant</span>
                            <div className="d-flex flex-wrap align-items-end gap-2 mb-2">
                              <div style={{ minWidth: 200 }}>
                                <label className="form-label small mb-0">Staff member</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={newAssistantId}
                                  onChange={(e) => setNewAssistantId(e.target.value)}
                                >
                                  <option value="">Select...</option>
                                  {employees
                                    .filter((e) => e.role !== 'manager' && !assistantManagers.some((a) => a.assistantId === e._id))
                                    .map((emp) => (
                                      <option key={emp._id} value={emp._id}>{dn(emp.username || emp.email)}</option>
                                    ))}
                                </select>
                              </div>
                              <label className="form-check form-check-inline mb-0">
                                <input type="checkbox" className="form-check-input" checked={newAssistantPermissions.includes('approve_holiday')} onChange={(e) => setNewAssistantPermissions(e.target.checked ? ['approve_holiday'] : [])} />
                                <span className="form-check-label small">Approve/deny holiday</span>
                              </label>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={!newAssistantId || savingAssistant}
                                onClick={async () => {
                                  if (!newAssistantId) return;
                                  setSavingAssistant(true);
                                  try {
                                    await hrApi.post('/api/managers/me/assistants', {
                                      assistantId: newAssistantId,
                                      permissions: newAssistantPermissions,
                                      canActOnBehalf: true,
                                    });
                                    const emp = employees.find((e) => e._id === newAssistantId);
                                    setAssistantManagers((prev) => [...prev, { assistantId: newAssistantId, username: emp?.username, email: emp?.email, permissions: newAssistantPermissions }]);
                                    setNewAssistantId('');
                                    setNewAssistantPermissions(['approve_holiday']);
                                  } catch (_) {}
                                  setSavingAssistant(false);
                                }}
                              >
                                {savingAssistant ? 'Adding...' : 'Add'}
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {showAddUserModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Add user</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => { setShowAddUserModal(false); setAddUserError(''); setAddUserForm({ username: '', email: '', password: '', role: 'employee' }); }} />
                      </div>
                      <form onSubmit={handleAddUser} autoComplete="off">
                        <div className="modal-body">
                          {addUserError && <div className="alert alert-danger py-2 mb-3">{addUserError}</div>}
                          <div className="mb-3">
                            <label className="form-label">Username</label>
                            <input
                              type="text"
                              className="form-control"
                              value={addUserForm.username}
                              onChange={(e) => setAddUserForm((f) => ({ ...f, username: e.target.value }))}
                              required
                              placeholder="Display name"
                              autoComplete="off"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Email</label>
                            <input
                              type="email"
                              className="form-control"
                              value={addUserForm.email}
                              onChange={(e) => setAddUserForm((f) => ({ ...f, email: e.target.value }))}
                              required
                              placeholder="user@example.com"
                              autoComplete="off"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Password</label>
                            <input
                              type="password"
                              className="form-control"
                              value={addUserForm.password}
                              onChange={(e) => setAddUserForm((f) => ({ ...f, password: e.target.value }))}
                              required
                              minLength={6}
                              placeholder="Min 6 characters"
                              autoComplete="new-password"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Role</label>
                            <select
                              className="form-select"
                              value={addUserForm.role}
                              onChange={(e) => setAddUserForm((f) => ({ ...f, role: e.target.value }))}
                            >
                              <option value="employee">Staff</option>
                              <option value="manager">Manager</option>
                              <option value="assistant_manager">Assistant Manager</option>
                            </select>
                            <p className="small text-muted mt-1 mb-0">Staff: employee dashboard. Manager: full manager access. Assistant Manager: Staff with permission to approve/deny holidays on behalf of a manager.</p>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowAddUserModal(false); setAddUserError(''); }}>Cancel</button>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={addUserLoading}>
                            {addUserLoading ? 'Creating…' : 'Create user'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {showEmployeeDefaultsModal && (settingsEmployeeId || calendarEmployeeId) && (
                <div className="modal d-block hr-override-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          Override: {dn(employees.find((e) => e._id === (settingsEmployeeId || calendarEmployeeId))?.username) || 'employee'}
                        </h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => { setShowEmployeeDefaultsModal(false); setSettingsEmployeeId(null); }} />
                      </div>
                      <div className="modal-body">
                        <p className="small text-muted mb-3">Values in effect for this employee. Change as needed; use the button below to reset to organization defaults.</p>
                        {loadingEmployeeDefaults ? (
                          <p className="small text-muted py-3">Loading...</p>
                        ) : (
                          <form onSubmit={handleSaveEmployeeDefaults}>
                            <div className="form-section mb-3">
                              <div className="row g-3 align-items-end">
                                <div className="col-sm-6 col-md-4">
                                  <label className="form-label small mb-0">Start time</label>
                                  <input type="time" className="form-control" value={employeeDefaultForm.defaultStartTime} onChange={handleEmployeeDefaultStartChange} />
                                </div>
                                <div className="col-sm-6 col-md-4">
                                  <label className="form-label small mb-0">End time</label>
                                  <input type="time" className="form-control" value={employeeDefaultForm.defaultEndTime} onChange={handleEmployeeDefaultEndChange} />
                                </div>
                                <div className="col-sm-6 col-md-4">
                                  <label className="form-label small mb-0">Total hours</label>
                                  <input type="number" className="form-control" min="0" max="24" step="any" value={employeeDefaultForm.defaultTotalHours} onChange={handleEmployeeDefaultTotalChange} />
                                </div>
                                <div className="col-sm-6 col-md-4">
                                  <label className="form-label small mb-0">Break start</label>
                                  <input type="time" className="form-control" value={employeeDefaultForm.breakStartTime} onChange={(e) => setEmployeeDefaultForm((f) => ({ ...f, breakStartTime: e.target.value }))} />
                                </div>
                                <div className="col-sm-6 col-md-4">
                                  <label className="form-label small mb-0">Break end</label>
                                  <input type="time" className="form-control" value={employeeDefaultForm.breakEndTime} onChange={(e) => setEmployeeDefaultForm((f) => ({ ...f, breakEndTime: e.target.value }))} />
                                </div>
                              </div>
                            </div>
                            <div className="form-section mb-3">
                              <span className="form-section-label">Working days</span>
                              <p className="small text-muted mb-2">Per weekday: Full day (9–18), Morning only (9–13), Afternoon only (14–18), or Off.</p>
                              <div className="d-flex flex-wrap gap-3 pt-1">
                                {[
                                  { d: 1, label: 'Mon' },
                                  { d: 2, label: 'Tue' },
                                  { d: 3, label: 'Wed' },
                                  { d: 4, label: 'Thu' },
                                  { d: 5, label: 'Fri' },
                                ].map(({ d, label }) => (
                                  <div key={d} className="d-flex align-items-center gap-1">
                                    <span className="small text-nowrap" style={{ width: 28 }}>{label}</span>
                                    <select
                                      className="form-select form-select-sm"
                                      style={{ width: 140 }}
                                      value={(employeeDefaultForm.daySchedule || {})[String(d)] || ((employeeDefaultForm.workingDays || []).includes(d) ? 'full' : 'off')}
                                      onChange={(e) => setEmployeeDaySchedule(d, e.target.value)}
                                    >
                                      <option value="off">Off</option>
                                      <option value="full">Full day</option>
                                      <option value="morning">Morning only</option>
                                      <option value="afternoon">Afternoon only</option>
                                    </select>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 align-items-center mt-4 pt-3 border-top">
                              <button type="submit" className="btn btn-primary" disabled={savingEmployeeDefaults}>
                                {savingEmployeeDefaults ? 'Saving...' : 'Save override'}
                              </button>
                              <button type="button" className="btn btn-outline-secondary" onClick={() => { setShowEmployeeDefaultsModal(false); setSettingsEmployeeId(null); }}>
                                Close
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger ms-2"
                                onClick={() => {
                                  handleResetEmployeeDefaultsToGlobal();
                                  setSettingsEmployeeId(null);
                                }}
                                disabled={savingEmployeeDefaults}
                              >
                                Use organization defaults
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showEmployeeAllowanceModal && editingAllowanceEmployeeId && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          Leave allowance – {dn(employees.find((e) => e._id === editingAllowanceEmployeeId)?.username) || 'employee'}
                        </h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => { setShowEmployeeAllowanceModal(false); setEditingAllowanceEmployeeId(null); }} />
                      </div>
                      <div className="modal-body">
                        <p className="small text-muted mb-2">
                          Set allowance (days per year) per leave type for this employee. Used and remaining are from approved leave.
                        </p>
                        {loadingEmployeeAllowance ? (
                          <p className="small text-muted">Loading...</p>
                        ) : (
                          <form onSubmit={handleSaveEmployeeAllowance}>
                            <div className="mb-2">
                              <label className="form-label small">Year</label>
                              <select
                                className="form-select form-select-sm"
                                style={{ minWidth: 100 }}
                                value={employeeAllowanceForm.year}
                                onChange={handleEmployeeAllowanceYearChange}
                              >
                                {[2025, 2026, 2027, 2028, 2029].map((y) => (
                                  <option key={y} value={y}>{y}</option>
                                ))}
                              </select>
                            </div>
                            {employeeAllowanceForm.allowances && employeeAllowanceForm.allowances.length > 0 ? (
                              <div className="mb-3">
                                {employeeAllowanceForm.allowances.map((a) => (
                                  <div key={a.leaveTypeCode} className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                    <label className="form-label small mb-0" style={{ minWidth: 120 }}>{a.name || a.leaveTypeCode}</label>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      min={0}
                                      max={366}
                                      style={{ width: 70 }}
                                      value={a.allowanceDays}
                                      onChange={(e) => handleEmployeeAllowanceDaysChange(a.leaveTypeCode, e.target.value)}
                                    />
                                    <span className="small text-muted">days</span>
                                    {(a.usedDays != null || a.remaining != null) && (
                                      <span className="small text-muted">(used: {a.usedDays ?? 0}, left: {a.remaining ?? 0})</span>
                                    )}
                                  </div>
                                ))}
                                {(employeeAllowanceForm.totalOver != null && employeeAllowanceForm.totalOver > 0) && (
                                  <p className="small text-danger fw-bold mb-2">
                                    {employeeAllowanceForm.totalOver} day{employeeAllowanceForm.totalOver !== 1 ? 's' : ''} over total allowance (vacation + carry over)
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="small text-muted">No leave types. Add types and default allowance first.</p>
                            )}
                            <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                              <button type="submit" className="btn btn-warning btn-sm" disabled={savingEmployeeAllowance || !employeeAllowanceForm.allowances?.length}>
                                {savingEmployeeAllowance ? 'Saving...' : 'Save'}
                              </button>
                              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowEmployeeAllowanceModal(false); setEditingAllowanceEmployeeId(null); }}>
                                Close
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showDefaultAllowanceModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Default leave allowance</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowDefaultAllowanceModal(false)} />
                      </div>
                      <div className="modal-body">
                        <p className="small text-muted mb-2">
                          Default days per leave type for the selected year. Applies to all employees unless overridden per employee.
                        </p>
                        {loadingDefaultAllowance ? (
                          <p className="small text-muted">Loading...</p>
                        ) : (
                          <form onSubmit={handleSaveDefaultAllowance}>
                            <div className="mb-3">
                              <label className="form-label small">Year</label>
                              <select
                                className="form-select form-select-sm"
                                style={{ minWidth: 100 }}
                                value={defaultAllowanceForm.year}
                                onChange={(e) => handleDefaultAllowanceYearChange(Number(e.target.value))}
                              >
                                {[2025, 2026, 2027, 2028, 2029].map((y) => (
                                  <option key={y} value={y}>{y}</option>
                                ))}
                              </select>
                            </div>
                            {defaultAllowanceForm.allowances && defaultAllowanceForm.allowances.length > 0 ? (
                              <div className="mb-3">
                                {defaultAllowanceForm.allowances.map((a) => (
                                  <div key={a.leaveTypeCode} className="d-flex align-items-center gap-2 mb-2">
                                    <label className="form-label small mb-0" style={{ minWidth: 140 }}>{a.name || a.leaveTypeCode}</label>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      min={0}
                                      max={366}
                                      style={{ width: 80 }}
                                      value={a.allowanceDays}
                                      onChange={(e) => handleDefaultAllowanceDaysChange(a.leaveTypeCode, e.target.value)}
                                    />
                                    <span className="small text-muted">days</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="small text-muted">No leave types defined. Add leave types first (e.g. run seed: <code>node scripts/seed-leave-types.js</code>).</p>
                            )}
                            <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                              <button type="submit" className="btn btn-warning btn-sm" disabled={savingDefaultAllowance || !defaultAllowanceForm.allowances?.length}>
                                {savingDefaultAllowance ? 'Saving...' : 'Save'}
                              </button>
                              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDefaultAllowanceModal(false)}>
                                Close
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showLeaveTypesModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Manage leave types</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowLeaveTypesModal(false)} />
                      </div>
                      <div className="modal-body">
                        <p className="small text-muted mb-3">Vacation leave and Previous year carryover are default, but you can add/remove leave types as needed.</p>
                        <div className="mb-3">
                          <label className="form-label small">Add leave type</label>
                          <div className="d-flex gap-2">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. Illness"
                              value={newLeaveTypeName}
                              onChange={(e) => setNewLeaveTypeName(e.target.value)}
                            />
                            <button
                              type="button"
                              className="btn btn-warning btn-sm"
                              disabled={!newLeaveTypeName.trim() || savingLeaveType}
                              onClick={() => {
                                setSavingLeaveType(true);
                                hrApi.post('/api/leave-types', { name: newLeaveTypeName.trim() })
                                  .then(() => {
                                    setNewLeaveTypeName('');
                                    return hrApi.get('/api/leave-types');
                                  })
                                  .then((res) => setLeaveTypes(Array.isArray(res.data) ? res.data : []))
                                  .catch((err) => setError(err?.response?.data?.message || 'Failed to add'))
                                  .finally(() => setSavingLeaveType(false));
                              }}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                        {leaveTypes.length === 0 ? (
                          <p className="small text-muted mb-0">No leave types yet.</p>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {leaveTypes.map((t) => (
                              <li key={t._id || t.code} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                <span>{t.name} <small className="text-muted">({t.code})</small></span>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => {
                                    if (!t._id) return;
                                    if (!window.confirm(`Remove "${t.name}"?`)) return;
                                    hrApi.delete(`/api/leave-types/${t._id}`)
                                      .then(() => hrApi.get('/api/leave-types'))
                                      .then((res) => setLeaveTypes(Array.isArray(res.data) ? res.data : []))
                                      .catch((err) => setError(err?.response?.data?.message || 'Failed to delete'));
                                  }}
                                  disabled={!t._id}
                                  title={!t._id ? 'Reload to manage this item' : 'Remove leave type'}
                                >
                                  Remove
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {teamTab === 'calendar' && (
                <>
                  {loadingList ? (
                    <p className="text-muted mb-3">Loading employees...</p>
                  ) : employees.length === 0 ? (
                    <p className="text-muted mb-3">No employees found.</p>
                  ) : (
                    <>
                      <div className="mb-3 d-flex flex-wrap align-items-end gap-2">
                        <div className="flex-grow-1" style={{ maxWidth: 360 }}>
                          <label className="form-label fw-medium">Show calendar for</label>
                          <select
                            className="form-select"
                            value={calendarEmployeeId}
                            onChange={(e) => {
                              const v = e.target.value;
                              setCalendarEmployeeId(v);
                              try {
                                if (v) localStorage.setItem(CALENDAR_EMPLOYEE_STORAGE_KEY, v);
                                else localStorage.removeItem(CALENDAR_EMPLOYEE_STORAGE_KEY);
                              } catch (_) {}
                            }}
                          >
                            <option value="">Select employee</option>
                            {employees.map((emp) => (
                              <option key={emp._id} value={emp._id}>
                                {dn(emp.username)}{emp.role === 'manager' ? ' (Manager)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {calendarEmployeeId ? (
                        <WorkingHoursCalendar
                          adminId={calendarEmployeeId}
                          applyManagerCorrectionLock
                          defaultsRefreshKey={defaultsRefreshKey}
                          approvedLeavesRefreshKey={holidayApprovalsRefreshKey}
                        />
                      ) : (
                        <p className="text-muted">Select an employee above to view their calendar.</p>
                      )}
                    </>
                  )}
                </>
              )}

              {teamTab === 'summary-export' && (
                <>
                  <div className="hr-section-card mb-4">
                    <h3 className="mb-2">Summary by date range</h3>
                    <p className="small text-muted mb-2">Total hours per employee between a start and end date.</p>
                    <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                      <div>
                        <label className="form-label small">From</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={summaryStartDate}
                          onChange={(e) => setSummaryStartDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label small">To</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={summaryEndDate}
                          onChange={(e) => setSummaryEndDate(e.target.value)}
                        />
                      </div>
                      <div className="d-flex gap-1 flex-wrap align-items-center">
                        <span className="small text-muted me-1">Presets:</span>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setSummaryRange(firstDayOfMonth(now.getFullYear(), now.getMonth() + 1), lastDayOfMonth(now.getFullYear(), now.getMonth() + 1))}>This month</button>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); setSummaryRange(firstDayOfMonth(d.getFullYear(), d.getMonth() + 1), lastDayOfMonth(d.getFullYear(), d.getMonth() + 1)); }}>Last month</button>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setSummaryRange(`${now.getFullYear()}-01-01`, `${now.getFullYear()}-12-31`)}>This year</button>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setSummaryRange(`${now.getFullYear() - 1}-01-01`, `${now.getFullYear() - 1}-12-31`)}>Last year</button>
                      </div>
                    </div>
                    {loadingSummary ? (
                      <p className="small text-muted">Loading...</p>
                    ) : (
                      <>
                        <div className="d-flex flex-wrap gap-3 mb-3 p-2 rounded" style={{ backgroundColor: 'rgba(31, 78, 121, 0.06)', border: '1px solid rgba(31, 78, 121, 0.12)' }}>
                          <span className="small"><strong>Grand total:</strong> <span style={{ color: 'var(--hr-primary, #1F4E79)' }}>{formatDecimalHours(summaryData.grandTotal)}</span></span>
                          <span className="small"><strong>Days in range:</strong> {summaryData.daysInRange}</span>
                          {summaryData.list.length > 0 && (
                            <span className="small"><strong>Employees with data:</strong> {summaryData.list.length}</span>
                          )}
                        </div>
                        <div className="hr-allowance-table-wrap">
                          <div className="table-responsive">
                            <table className="table table-sm hr-allowance-table mb-0">
                              <thead><tr><th>Employee</th><th>Email</th><th>Days with entries</th><th>Total hours</th></tr></thead>
                              <tbody>
                                {(summaryData.list || []).map((row) => (
                                  <tr key={row.adminId}>
                                    <td className="hr-allowance-name">{dn(row.username)}</td>
                                    <td className="text-muted small">{row.email}</td>
                                    <td>{row.daysCount != null ? row.daysCount : '–'}</td>
                                    <td>{formatDecimalHours(row.totalHours)}</td>
                                  </tr>
                                ))}
                                {(!summaryData.list || summaryData.list.length === 0) && <tr><td colSpan="4" className="text-muted">No data for this range</td></tr>}
                              </tbody>
                              {summaryData.list && summaryData.list.length > 0 && (
                                <tfoot>
                                  <tr className="fw-bold"><td colSpan="2">Total</td><td>–</td><td>{formatDecimalHours(summaryData.grandTotal)}</td></tr>
                                </tfoot>
                              )}
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="hr-section-card">
                    <h3 className="mb-2">Export</h3>
                    <p className="small text-muted mb-3">Download working hours for a date range as CSV or PDF. Choose employees, report type, and format below.</p>
                    <div className="row g-3 mb-3">
                      <div className="col-auto">
                        <label className="form-label small text-muted mb-0">From</label>
                        <input type="date" className="form-control form-control-sm" value={exportStartDate} onChange={(e) => setExportStartDate(e.target.value)} />
                      </div>
                      <div className="col-auto">
                        <label className="form-label small text-muted mb-0">To</label>
                        <input type="date" className="form-control form-control-sm" value={exportEndDate} onChange={(e) => setExportEndDate(e.target.value)} />
                      </div>
                      <div className="col-auto d-flex align-items-end gap-1 flex-wrap">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setExportRange(firstDayOfMonth(now.getFullYear(), now.getMonth() + 1), lastDayOfMonth(now.getFullYear(), now.getMonth() + 1))}>This month</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { const d = new Date(now.getFullYear(), now.getMonth() - 1, 1); setExportRange(firstDayOfMonth(d.getFullYear(), d.getMonth() + 1), lastDayOfMonth(d.getFullYear(), d.getMonth() + 1)); }}>Last month</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setExportRange(`${now.getFullYear()}-01-01`, `${now.getFullYear()}-12-31`)}>This year</button>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small text-muted mb-1">Employees</label>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm text-start d-block"
                        style={{ minWidth: 200 }}
                        onClick={openExportEmployeesModal}
                      >
                        <i className="bi bi-people me-1" />
                        {exportAdminIds.length === 0 ? 'All employees' : `${exportAdminIds.length} selected`}
                      </button>
                    </div>

                    {showExportEmployeesModal && (
                      <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">Select employees for export</h5>
                              <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowExportEmployeesModal(false)} />
                            </div>
                            <div className="modal-body">
                              <p className="small text-muted mb-3">Leave all unchecked to include everyone.</p>
                              <div className="d-flex gap-2 mb-3">
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={selectAllExportModal}>Select all</button>
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearExportModal}>Clear</button>
                              </div>
                              <div className="border rounded p-2" style={{ maxHeight: 320, overflowY: 'auto' }}>
                                {employees.length === 0 ? (
                                  <p className="text-muted small mb-0">No employees loaded.</p>
                                ) : (
                                  <div className="d-flex flex-column gap-1">
                                    {employees.map((emp) => (
                                      <label key={emp._id} className="d-flex align-items-center gap-2 py-1 px-2 rounded mb-0" style={{ cursor: 'pointer' }}>
                                        <input
                                          type="checkbox"
                                          className="form-check-input flex-shrink-0"
                                          checked={exportModalSelectedIds.includes(emp._id)}
                                          onChange={() => toggleExportModalEmployee(emp._id)}
                                        />
                                        <span>{dn(emp.username)}{emp.role === 'manager' ? ' (Manager)' : ''}</span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <p className="small text-muted mt-2 mb-0">
                                {exportModalSelectedIds.length === 0 ? 'All employees' : `${exportModalSelectedIds.length} selected`}
                              </p>
                            </div>
                            <div className="modal-footer">
                              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowExportEmployeesModal(false)}>Cancel</button>
                              <button type="button" className="btn btn-primary btn-sm" onClick={saveExportEmployeesModal}>Apply</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted">Report</span>
                        <div className="btn-group btn-group-sm" role="group">
                          <input type="radio" className="btn-check" name="exportReportType" id="exportReportDaily" value="daily" checked={exportReportType === 'daily'} onChange={() => setExportReportType('daily')} />
                          <label className="btn btn-outline-secondary" htmlFor="exportReportDaily">Daily log</label>
                          <input type="radio" className="btn-check" name="exportReportType" id="exportReportSummary" value="summary" checked={exportReportType === 'summary'} onChange={() => setExportReportType('summary')} />
                          <label className="btn btn-outline-secondary" htmlFor="exportReportSummary">Summary</label>
                        </div>
                        <span
                          className="text-muted"
                          style={{ cursor: 'help', fontSize: '0.95rem' }}
                          title={'Daily log: one row per employee per day with timings and hours.\nSummary: one row per employee for the period with totals and average.'}
                          aria-label="Report type info"
                        >
                          <i className="bi bi-info-circle" />
                        </span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted">Format</span>
                        <div className="btn-group btn-group-sm" role="group">
                          <input type="radio" className="btn-check" name="exportFormat" id="exportFormatCsv" value="csv" checked={exportFormat === 'csv'} onChange={() => setExportFormat('csv')} />
                          <label className="btn btn-outline-secondary" htmlFor="exportFormatCsv">CSV</label>
                          <input type="radio" className="btn-check" name="exportFormat" id="exportFormatPdf" value="pdf" checked={exportFormat === 'pdf'} onChange={() => setExportFormat('pdf')} />
                          <label className="btn btn-outline-secondary" htmlFor="exportFormatPdf">PDF</label>
                        </div>
                      </div>
                    </div>

                    <div className="pt-1">
                    <button type="button" className="btn btn-primary" onClick={() => handleExport(exportReportType, exportFormat)} disabled={exporting}>
                      {exporting ? 'Exporting…' : 'Download'}
                    </button>
                    </div>
                  </div>
                </>
              )}

              {teamTab === 'holiday-approvals' && (
                <>
                  <div className="hr-section-card">
                    <p className="small text-muted mb-3">All leave requests and their status. You can approve or reject pending requests, or change an existing decision (e.g. approve a rejected request or reject an approved one).</p>

                    {pendingHolidays.length > 0 && (
                      <div className="d-flex flex-wrap align-items-end gap-3 mb-3 p-3 rounded" style={{ backgroundColor: 'var(--hr-panel, #F8F5F0)', border: '1px solid rgba(0,87,60,0.12)' }}>
                        <div style={{ minWidth: 160 }}>
                          <label className="form-label small mb-1 fw-medium">Employee</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Filter by name..."
                            value={approvalFilterEmployee}
                            onChange={(e) => setApprovalFilterEmployee(e.target.value)}
                          />
                        </div>
                        <div style={{ minWidth: 140 }}>
                          <label className="form-label small mb-1 fw-medium">Status</label>
                          <select
                            className="form-select form-select-sm"
                            value={approvalFilterStatus}
                            onChange={(e) => setApprovalFilterStatus(e.target.value)}
                          >
                            <option value="">All</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </div>
                        <div style={{ minWidth: 140 }}>
                          <label className="form-label small mb-1 fw-medium">From date</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={approvalFilterDateFrom}
                            onChange={(e) => setApprovalFilterDateFrom(e.target.value)}
                          />
                        </div>
                        <div style={{ minWidth: 140 }}>
                          <label className="form-label small mb-1 fw-medium">To date</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={approvalFilterDateTo}
                            onChange={(e) => setApprovalFilterDateTo(e.target.value)}
                          />
                        </div>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearApprovalFilters}>
                          Clear filters
                        </button>
                        <span className="small text-muted ms-auto align-self-center">
                          {filteredApprovalHolidays.length} of {pendingHolidays.length} request(s)
                        </span>
                      </div>
                    )}

                    {pendingHolidays.length === 0 ? (
                      <p className="text-muted mb-0">No leave requests yet.</p>
                    ) : filteredApprovalHolidays.length === 0 ? (
                      <p className="text-muted mb-0">No requests match the filters. Try adjusting or clearing the filters.</p>
                    ) : (
                      <div className="hr-allowance-table-wrap">
                        <div className="table-responsive">
                          <table className="table table-sm hr-allowance-table mb-0">
                            <thead>
                              <tr>
                                <th>Employee</th>
                                <th>From</th>
                                <th>To</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Decided by</th>
                                <th className="text-end" style={{ width: 180 }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredApprovalHolidays.map((r) => (
                                <tr key={r._id}>
                                  <td className="hr-allowance-name">{r.adminId && typeof r.adminId === 'object' ? dn(r.adminId.username) : r.adminId || '–'}</td>
                                  <td>{new Date(r.startDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}</td>
                                  <td>{new Date(r.endDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}</td>
                                  <td className="small">{r.leaveTypeCode || 'annual'}</td>
                                  <td>
                                    <span className={`badge ${r.status === 'approved' ? 'bg-success' : r.status === 'rejected' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                      {r.status === 'pending' ? 'Pending' : r.status === 'approved' ? 'Approved' : 'Rejected'}
                                    </span>
                                  </td>
                                  <td className="small">{formatDecidedBy(r)}</td>
                                  <td className="text-end">
                                    <div className="d-flex gap-1 justify-content-end">
                                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => { setHolidayViewRequest(r); setViewModalRejectReason(''); }}>View</button>
                                      {r.status === 'pending' && (
                                        <>
                                          <button type="button" className="btn btn-success btn-sm" onClick={() => handleApproveHoliday(r._id, r.status)}>Approve</button>
                                          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => { setHolidayRejectModal({ id: r._id, status: r.status }); setHolidayRejectReason(''); }}>Reject</button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>

                  {holidayRejectModal && (
                    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                      <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">Reject request</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={closeHolidayDecisionModals} />
                          </div>
                          <div className="modal-body">
                            <label className="form-label">Reason (required)</label>
                            <textarea
                              className="form-control"
                              rows={3}
                              placeholder="e.g. Conflict with team coverage"
                              value={holidayRejectReason}
                              onChange={(e) => setHolidayRejectReason(e.target.value)}
                            />
                          </div>
                          <div className="modal-footer">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={closeHolidayDecisionModals}>Cancel</button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              disabled={!holidayRejectReason.trim()}
                              onClick={handleRejectHoliday}
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {holidayEditModal && (
                    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                      <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">Change decision</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={closeHolidayDecisionModals} />
                          </div>
                          <div className="modal-body">
                            <p className="small text-muted mb-3">Approve or reject this request.</p>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                              <button
                                type="button"
                                className="btn btn-success btn-sm"
                                onClick={() => handleApproveHoliday(holidayEditModal.id)}
                              >
                                Approve
                              </button>
                            </div>
                            <label className="form-label">Reason (required for reject)</label>
                            <textarea
                              className="form-control"
                              rows={3}
                              placeholder="e.g. Conflict with team coverage"
                              value={holidayRejectReason}
                              onChange={(e) => setHolidayRejectReason(e.target.value)}
                            />
                          </div>
                          <div className="modal-footer">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={closeHolidayDecisionModals}>Cancel</button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              disabled={!holidayRejectReason.trim()}
                              onClick={handleRejectHoliday}
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {holidayViewRequest && (
                    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040 }} tabIndex={-1}>
                      <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">Leave request details</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={closeHolidayViewModal} />
                          </div>
                          <div className="modal-body">
                            <dl className="row mb-0">
                              <dt className="col-sm-4 text-muted small">Employee</dt>
                              <dd className="col-sm-8">{holidayViewRequest.adminId && typeof holidayViewRequest.adminId === 'object' ? dn(holidayViewRequest.adminId.username) : holidayViewRequest.adminId || '–'}</dd>

                              <dt className="col-sm-4 text-muted small">From</dt>
                              <dd className="col-sm-8">{new Date(holidayViewRequest.startDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}</dd>

                              <dt className="col-sm-4 text-muted small">To</dt>
                              <dd className="col-sm-8">{new Date(holidayViewRequest.endDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}</dd>

                              <dt className="col-sm-4 text-muted small">Type</dt>
                              <dd className="col-sm-8">{holidayViewRequest.leaveTypeCode || 'annual'}</dd>

                              <dt className="col-sm-4 text-muted small">Days taken</dt>
                              <dd className="col-sm-8">
                                <span className="d-inline-flex align-items-center gap-1">
                                  {typeof getDaysTaken(holidayViewRequest) === 'number' ? getDaysTaken(holidayViewRequest) : '–'}
                                  {holidayViewRequest.startDate && holidayViewRequest.endDate && (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0 text-secondary"
                                      onClick={() => setHolidayViewRequest((prev) => ({ ...prev, _showBreakdown: true }))}
                                      title="Show breakdown"
                                      aria-label="Show days breakdown"
                                    >
                                      <i className="bi bi-calendar3" />
                                    </button>
                                  )}
                                </span>
                              </dd>

                              <dt className="col-sm-4 text-muted small">Reason for Absence</dt>
                              <dd className="col-sm-8">{holidayViewRequest.comment || '–'}</dd>

                              <dt className="col-sm-4 text-muted small">Attachments</dt>
                              <dd className="col-sm-8">
                                {(holidayViewRequest.attachments && holidayViewRequest.attachments.length > 0)
                                  ? holidayViewRequest.attachments.map((att, i) => (
                                      <button
                                        key={i}
                                        type="button"
                                        className="btn btn-link btn-sm p-0 me-2 text-primary text-decoration-none d-inline-block"
                                        onClick={() => downloadHolidayAttachment(holidayViewRequest._id, att)}
                                        title={`Download ${att.originalName}`}
                                      >
                                        <i className="bi bi-paperclip me-1" />
                                        {att.originalName}
                                      </button>
                                    ))
                                  : '–'}
                              </dd>

                              <dt className="col-sm-4 text-muted small">Status</dt>
                              <dd className="col-sm-8">
                                <span className={`badge ${holidayViewRequest.status === 'approved' ? 'bg-success' : holidayViewRequest.status === 'rejected' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                  {holidayViewRequest.status === 'pending' ? 'Pending' : holidayViewRequest.status === 'approved' ? 'Approved' : 'Rejected'}
                                </span>
                              </dd>

                              {(holidayViewRequest.status === 'approved' || holidayViewRequest.status === 'rejected') && (
                                <>
                                  <dt className="col-sm-4 text-muted small">Decided by</dt>
                                  <dd className="col-sm-8">{formatDecidedBy(holidayViewRequest)}</dd>
                                </>
                              )}

                              {holidayViewRequest.status === 'rejected' && (
                                <>
                                  <dt className="col-sm-4 text-muted small">Rejected reason</dt>
                                  <dd className="col-sm-8">{holidayViewRequest.rejectedReason || '–'}</dd>
                                </>
                              )}
                            </dl>

                            {(holidayViewRequest.status === 'pending' || holidayViewRequest.status === 'approved' || holidayViewRequest.status === 'rejected') && (
                              <div className="mt-4 pt-3 border-top">
                                <p className="small fw-medium mb-2">Change decision</p>
                                <div className="d-flex flex-wrap gap-2 align-items-start">
                                  {/* Pending: show Approve and Reject. Approved: show only Reject. Rejected: show only Approve. */}
                                  {(holidayViewRequest.status === 'pending' || holidayViewRequest.status === 'rejected') && (
                                    <button type="button" className="btn btn-success btn-sm" onClick={handleApproveFromViewModal}>
                                      Approve
                                    </button>
                                  )}
                                  {(holidayViewRequest.status === 'pending' || holidayViewRequest.status === 'approved') && (
                                    !holidayViewRequest._showReject ? (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => setHolidayViewRequest((prev) => ({ ...prev, _showReject: true }))}
                                      >
                                        Reject
                                      </button>
                                    ) : (
                                      <div className="flex-grow-1" style={{ minWidth: 280 }}>
                                        <textarea
                                          className="form-control form-control-sm mb-2"
                                          rows={2}
                                          placeholder="Reason for rejection (required)"
                                          value={viewModalRejectReason}
                                          onChange={(e) => setViewModalRejectReason(e.target.value)}
                                        />
                                        <div className="d-flex gap-2">
                                          <button type="button" className="btn btn-danger btn-sm" disabled={!viewModalRejectReason.trim()} onClick={handleRejectFromViewModal}>
                                            Confirm reject
                                          </button>
                                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setHolidayViewRequest((prev) => ({ ...prev, _showReject: false }))}>
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={closeHolidayViewModal}>
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {holidayViewRequest?._showBreakdown && (
                    <DaysTakenBreakdownModal
                      show={!!holidayViewRequest?._showBreakdown}
                      onClose={() => setHolidayViewRequest((prev) => prev && { ...prev, _showBreakdown: false })}
                      startDate={holidayViewRequest?.startDate}
                      endDate={holidayViewRequest?.endDate}
                      publicHolidays={approvalPublicHolidays}
                      daysTaken={typeof getDaysTaken(holidayViewRequest) === 'number' ? getDaysTaken(holidayViewRequest) : 0}
                      employeeWorkingSettings={(() => {
                        const id = holidayRequestAdminIdStr(holidayViewRequest);
                        return (id && approvalEmployeeSchedules[id]) || null;
                      })()}
                    />
                  )}

                  <p className="small text-muted mt-3 mb-0">To manage annual allowance per employee, go to the <strong>Holiday calendar</strong> tab.</p>
                </>
              )}

              {teamTab === 'overtime-approvals' && (
                <>
                  <div className="hr-section-card">
                    <p className="small text-muted mb-3">All overtime requests and their status. You can approve or reject requests.</p>
                    {overtimeRequests.length > 0 && (
                      <div className="d-flex flex-wrap align-items-end gap-3 mb-3 p-3 rounded" style={{ backgroundColor: 'var(--hr-panel, #F8F5F0)', border: '1px solid rgba(0,87,60,0.12)' }}>
                        <div style={{ minWidth: 160 }}>
                          <label className="form-label small mb-1 fw-medium">Employee</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Filter by name..."
                            value={overtimeFilterEmployee}
                            onChange={(e) => setOvertimeFilterEmployee(e.target.value)}
                          />
                        </div>
                        <div style={{ minWidth: 140 }}>
                          <label className="form-label small mb-1 fw-medium">Status</label>
                          <select
                            className="form-select form-select-sm"
                            value={overtimeFilterStatus}
                            onChange={(e) => setOvertimeFilterStatus(e.target.value)}
                          >
                            <option value="">All</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </div>
                        <div style={{ minWidth: 140 }}>
                          <label className="form-label small mb-1 fw-medium">Applied from</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={overtimeFilterDateFrom}
                            onChange={(e) => setOvertimeFilterDateFrom(e.target.value)}
                          />
                        </div>
                        <div style={{ minWidth: 140 }}>
                          <label className="form-label small mb-1 fw-medium">Applied to</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={overtimeFilterDateTo}
                            onChange={(e) => setOvertimeFilterDateTo(e.target.value)}
                          />
                        </div>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearOvertimeFilters}>
                          Clear filters
                        </button>
                        <span className="small text-muted ms-auto align-self-center">
                          {filteredOvertimeRequests.length} of {overtimeRequests.length} request(s)
                        </span>
                      </div>
                    )}
                    {overtimeDecisionMessage && (
                      <div className="alert alert-success py-2 mb-3" role="status" aria-live="polite">
                        {overtimeDecisionMessage}
                      </div>
                    )}
                    {overtimeDecisionError && (
                      <div className="alert alert-danger py-2 mb-3">
                        {overtimeDecisionError}
                      </div>
                    )}
                    {overtimeRequests.length === 0 ? (
                      <p className="text-muted mb-0">No overtime requests yet.</p>
                    ) : filteredOvertimeRequests.length === 0 ? (
                      <p className="text-muted mb-0">No requests match the filters. Try adjusting or clearing the filters.</p>
                    ) : (
                      <OvertimeRequestList
                        requests={filteredOvertimeRequests}
                        showEmployeeName
                        onView={(r) => setOvertimeViewRequest(r)}
                        onApprove={handleApproveOvertime}
                        onReject={handleRejectOvertime}
                        decisionLoadingId={overtimeDecisionLoadingId}
                        canDecide={
                          me?.role === 'manager'
                          || me?.role === 'superadmin'
                          || (me?.role === 'assistant_manager'
                            && Array.isArray(me?.assistantManager?.permissions)
                            && me.assistantManager.permissions.includes('approve_holiday'))
                        }
                      />
                    )}
                  </div>

                  {overtimeRejectModal && (
                    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                      <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">Reject overtime request</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={() => { setOvertimeRejectModal(null); setOvertimeRejectReason(''); }} />
                          </div>
                          <div className="modal-body">
                            <label className="form-label">Reason (required)</label>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={overtimeRejectReason}
                              onChange={(e) => setOvertimeRejectReason(e.target.value)}
                              placeholder="e.g. Not enough details"
                            />
                          </div>
                          <div className="modal-footer">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setOvertimeRejectModal(null); setOvertimeRejectReason(''); }}>Cancel</button>
                            <button type="button" className="btn btn-danger btn-sm" disabled={!overtimeRejectReason.trim()} onClick={confirmRejectOvertime}>Reject</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {overtimeApproveModal && (
                    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                      <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">Approve overtime</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={() => { setOvertimeApproveModal(null); setOvertimeApproveDays(''); }} />
                          </div>
                          <div className="modal-body">
                            {overtimeApproveModal.employeeName ? (
                              <p className="small text-muted mb-2">Employee: <strong>{dn(overtimeApproveModal.employeeName)}</strong></p>
                            ) : null}
                            <label className="form-label" htmlFor="overtime-approve-days">Amount to grant</label>
                            <input
                              id="overtime-approve-days"
                              type="number"
                              className="form-control"
                              min="0.01"
                              step="0.01"
                              value={overtimeApproveDays}
                              onChange={(e) => setOvertimeApproveDays(e.target.value)}
                              placeholder="e.g. 3"
                            />
                            <p className="small text-muted mt-2 mb-0">Enter a number greater than zero (same unit as annual leave).</p>
                          </div>
                          <div className="modal-footer">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setOvertimeApproveModal(null); setOvertimeApproveDays(''); }}>Cancel</button>
                            <button
                              type="button"
                              className="btn btn-success btn-sm"
                              disabled={!Number.isFinite(Number(overtimeApproveDays)) || Number(overtimeApproveDays) <= 0}
                              onClick={confirmOvertimeApproveWithDays}
                            >
                              Confirm approval
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {overtimeViewRequest && (
                    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040 }} tabIndex={-1}>
                      <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">Overtime request details</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={closeOvertimeViewModal} />
                          </div>
                          <div className="modal-body">
                            <dl className="row mb-0">
                              <dt className="col-sm-4 text-muted small">Employee</dt>
                              <dd className="col-sm-8">
                                {overtimeViewRequest.adminId && typeof overtimeViewRequest.adminId === 'object'
                                  ? dn(overtimeViewRequest.adminId.username)
                                  : overtimeViewRequest.adminId || '–'}
                              </dd>
                              <dt className="col-sm-4 text-muted small">Applied on</dt>
                              <dd className="col-sm-8">
                                {overtimeViewRequest.createdAt
                                  ? new Date(overtimeViewRequest.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })
                                  : '–'}
                              </dd>
                              <dt className="col-sm-4 text-muted small">Reason for overtime</dt>
                              <dd className="col-sm-8" style={{ whiteSpace: 'pre-wrap' }}>{displayOvertimeReasonForView(overtimeViewRequest)}</dd>
                              <dt className="col-sm-4 text-muted small">Status</dt>
                              <dd className="col-sm-8">
                                <span className={`badge ${overtimeViewRequest.status === 'approved' ? 'bg-success' : overtimeViewRequest.status === 'rejected' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                  {overtimeViewRequest.status === 'pending' ? 'Pending' : overtimeViewRequest.status === 'approved' ? 'Approved' : 'Rejected'}
                                </span>
                              </dd>
                              {overtimeViewRequest.status === 'approved' && (
                                <>
                                  <dt className="col-sm-4 text-muted small">Granted</dt>
                                  <dd className="col-sm-8">
                                    {overtimeViewRequest.approvedDays != null && Number.isFinite(Number(overtimeViewRequest.approvedDays))
                                      ? overtimeViewRequest.approvedDays
                                      : '–'}
                                  </dd>
                                </>
                              )}
                              {(overtimeViewRequest.status === 'approved' || overtimeViewRequest.status === 'rejected') && (
                                <>
                                  <dt className="col-sm-4 text-muted small">Decided by</dt>
                                  <dd className="col-sm-8">{formatDecidedBy(overtimeViewRequest)}</dd>
                                </>
                              )}
                              {overtimeViewRequest.status === 'rejected' && (
                                <>
                                  <dt className="col-sm-4 text-muted small">Rejected reason</dt>
                                  <dd className="col-sm-8">{overtimeViewRequest.rejectedReason || '–'}</dd>
                                </>
                              )}
                            </dl>
                            {(me?.role === 'manager'
                              || me?.role === 'superadmin'
                              || (me?.role === 'assistant_manager'
                                && Array.isArray(me?.assistantManager?.permissions)
                                && me.assistantManager.permissions.includes('approve_holiday'))) && (
                              <div className="mt-4 pt-3 border-top">
                                <p className="small fw-medium mb-2">Change decision</p>
                                <div className="d-flex flex-wrap gap-2">
                                  {(overtimeViewRequest.status === 'pending' || overtimeViewRequest.status === 'rejected') && (
                                    <button type="button" className="btn btn-success btn-sm" onClick={overtimeViewRequest.status === 'rejected' ? handleReapproveOvertimeFromView : handleApproveOvertimeFromView}>
                                      Approve
                                    </button>
                                  )}
                                  {(overtimeViewRequest.status === 'pending' || overtimeViewRequest.status === 'approved') && (
                                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleRejectOvertimeFromView}>
                                      Reject
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={closeOvertimeViewModal}>
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {teamTab === 'activity' && (
                <div className="hr-section-card">
                  <div className="d-flex justify-content-end align-items-center mb-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setLoadingActivity(true); hrApi.get('/api/activity', { params: { limit: 100 } }).then((res) => setActivityDecisions(Array.isArray(res.data) ? res.data : [])).catch(() => setActivityDecisions([])).finally(() => setLoadingActivity(false)); }}>Refresh</button>
                  </div>
                  {loadingActivity && <p className="text-muted small">Loading…</p>}
                  {!loadingActivity && activityDecisions.length === 0 && <p className="text-muted small mb-0">No activity recorded yet.</p>}
                  {!loadingActivity && activityDecisions.length > 0 && (
                    <div className="table-responsive hr-allowance-table-wrap">
                      <table className="table table-sm hr-allowance-table mb-0">
                        <thead>
                          <tr>
                            <th>Date & time</th>
                            <th>Action</th>
                            <th>By</th>
                            <th>Target / subject</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activityDecisions.map((r) => (
                            <tr key={`${r.type}-${r._id}`}>
                              <td className="small">{new Date(r.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</td>
                              {r.type === 'holiday_decision' ? (
                                <>
                                  <td>
                                    <span className={`badge ${(r.action || r.status) === 'approved' ? 'bg-success' : 'bg-danger'}`}>
                                      {(r.action || r.status) === 'approved' ? 'Holiday approved' : 'Holiday rejected'}
                                    </span>
                                  </td>
                                  <td className="small">{formatAuthority(r)}</td>
                                  <td className="hr-allowance-name small">{r.adminId && typeof r.adminId === 'object' ? dn(r.adminId.username) : r.adminId || '–'}</td>
                                  <td className="small">
                                    {new Date(r.startDate).toLocaleDateString(undefined, { dateStyle: 'medium' })} – {new Date(r.endDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                                    {formatPerformedBy(r) && formatPerformedBy(r) !== '–' ? ` · Performed by: ${formatPerformedBy(r)}` : ''}
                                  </td>
                                </>
                              ) : r.type === 'overtime_decision' ? (
                                <>
                                  <td>
                                    <span className={`badge ${r.action === 'approved' ? 'bg-success' : 'bg-danger'}`}>
                                      {r.action === 'approved' ? 'Overtime approved' : 'Overtime rejected'}
                                    </span>
                                  </td>
                                  <td className="small">{formatAuthority(r)}</td>
                                  <td className="hr-allowance-name small">{r.adminId && typeof r.adminId === 'object' ? dn(r.adminId.username) : r.adminId || '–'}</td>
                                  <td className="small">
                                    {r.reason || '–'}
                                    {r.action === 'approved' && r.approvedDays != null ? ` · Granted: ${r.approvedDays}` : ''}
                                    {r.action === 'rejected' && r.rejectedReason ? ` · Reason: ${r.rejectedReason}` : ''}
                                    {formatPerformedBy(r) && formatPerformedBy(r) !== '–' ? ` · Performed by: ${formatPerformedBy(r)}` : ''}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="small">{r.actionLabel || r.action}</td>
                                  <td className="small">{r.performedByEmail || '–'}</td>
                                  <td className="small">{r.targetUserEmail || '–'}</td>
                                  <td className="small text-muted">
                                    {r.details && typeof r.details === 'object' ? (
                                      Object.keys(r.details).length <= 3
                                        ? Object.entries(r.details).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')
                                        : `${Object.keys(r.details).length} fields`
                                    ) : '–'}
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {teamTab === 'holiday' && (
                <>
                  <div className="hr-section-card mb-4">
                    <h3 className="mb-2">Calendar</h3>
                    <p className="small text-muted mb-3">Public holidays and employee absences (approved leave). Click a day with absences to see who is off and why.</p>
                    <HolidayCalendar refreshKey={publicHolidaysRefreshKey} />
                  </div>

                  <div className="hr-section-card mb-4">
                    <h3 className="mb-2">Per-employee override</h3>
                    <p className="small text-muted mb-3">Select an employee to set their allowance (overrides the default for that year).</p>
                    <div className="mb-0 d-flex flex-wrap align-items-end gap-2">
                      <div style={{ maxWidth: 360 }}>
                        <label className="form-label fw-medium">Select employee</label>
                        <select
                          className="form-select"
                          value={allowanceEmployeeId}
                          onChange={(e) => setAllowanceEmployeeId(e.target.value)}
                        >
                          <option value="">Select employee</option>
                          {employees.map((emp) => (
                            <option key={emp._id} value={emp._id}>
                              {dn(emp.username)}{emp.role === 'manager' ? ' (Manager)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      {allowanceEmployeeId && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => openEmployeeAllowanceModal(allowanceEmployeeId)}
                        >
                          <i className="bi bi-briefcase me-1" /> Annual allowance for this employee
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="hr-section-card">
                    <h3 className="mb-2">Allowance overview</h3>
                    <p className="small text-muted mb-3">Allowance, used and remaining days per employee for the selected year.</p>
                    <div className="mb-3">
                      <label className="form-label small">Year</label>
                      <select
                        className="form-select form-select-sm"
                        style={{ minWidth: 100 }}
                        value={allowanceListYear}
                        onChange={(e) => setAllowanceListYear(Number(e.target.value))}
                      >
                        {[2025, 2026, 2027, 2028].map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    {loadingAllowanceList ? (
                      <p className="text-muted small">Loading...</p>
                    ) : allowanceList.length === 0 ? (
                      <p className="text-muted small mb-0">No employees found.</p>
                    ) : (
                      <div className="table-responsive hr-allowance-table-wrap">
                        <table className="table hr-allowance-table">
                          <thead>
                            <tr>
                              <th>Employee</th>
                              <th>Total allowance</th>
                              <th>Total used</th>
                              <th>Total remaining</th>
                              <th className="text-end" style={{ width: 120 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {allowanceList.map((row) => {
                              const allowances = row.allowances || [];
                              const totalAllowance = allowances.reduce((s, a) => s + (a.allowanceDays || 0), 0);
                              const totalUsed = allowances.reduce((s, a) => s + (a.usedDays || 0), 0);
                              const totalRemaining = allowances.reduce((s, a) => s + (a.remaining ?? 0), 0);
                              const totalOver = row.totalOver != null ? row.totalOver : 0;
                              const isOver = totalOver > 0;
                              const rowId = row.adminId?.toString?.() || row.adminId;
                              const isExpanded = expandedAllowanceEmployeeId === rowId;
                              const overtimeBal = row.overtimeBalance || { total: 0, usedDays: 0, remaining: 0 };
                              return (
                                <React.Fragment key={rowId}>
                                  <tr className={isOver ? 'table-danger' : ''}>
                                    <td className="hr-allowance-name">{dn(row.username || row.email || row.adminId)}</td>
                                    {allowances.length > 0 ? (
                                      <>
                                        <td>{totalAllowance}</td>
                                        <td>{totalUsed}</td>
                                        <td className={`hr-allowance-remaining ${isOver ? 'text-danger fw-bold' : ''}`}>
                                          {totalRemaining}
                                          {isOver && <span className="ms-1">({totalOver} over)</span>}
                                        </td>
                                        <td className="text-end">
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary text-nowrap"
                                            onClick={() => setExpandedAllowanceEmployeeId(isExpanded ? null : rowId)}
                                          >
                                            {isExpanded ? 'Hide details' : 'View details'}
                                          </button>
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td colSpan={3} className="text-muted small">No allowance data</td>
                                        <td className="text-end">
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary text-nowrap"
                                            onClick={() => setExpandedAllowanceEmployeeId(isExpanded ? null : rowId)}
                                          >
                                            {isExpanded ? 'Hide details' : 'View details'}
                                          </button>
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                  {isExpanded && (
                                    <>
                                      {allowances.map((a, idx) => (
                                        <tr key={`${rowId}-${a.leaveTypeCode}-${idx}`} className="table-light">
                                          <td className="ps-4 small text-muted">{a.name || a.leaveTypeCode}</td>
                                          <td>{a.allowanceDays}</td>
                                          <td>{a.usedDays}</td>
                                          <td className="hr-allowance-remaining">{a.remaining}</td>
                                          <td></td>
                                        </tr>
                                      ))}
                                      <tr key={`${rowId}-overtime`} className="table-light">
                                        <td className="ps-4 small text-muted">
                                          Overtime
                                          <span className="d-block text-muted" style={{ fontSize: '0.8em' }}>
                                            From approved overtime requests (not part of default annual allowance)
                                          </span>
                                        </td>
                                        <td>{overtimeBal.total}</td>
                                        <td>{overtimeBal.usedDays}</td>
                                        <td className="hr-allowance-remaining">{overtimeBal.remaining}</td>
                                        <td></td>
                                      </tr>
                                      {totalOver > 0 && (
                                        <tr className="table-danger">
                                          <td colSpan={4} className="ps-4 small text-danger fw-bold">
                                            {totalOver} day{totalOver !== 1 ? 's' : ''} over total allowance (vacation + carry over)
                                          </td>
                                          <td></td>
                                        </tr>
                                      )}
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}

              {showPublicHolidayManageModal && (
                <div className="modal d-block hr-public-holidays-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 960 }}>
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Manage public holidays</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowPublicHolidayManageModal(false)} />
                      </div>
                      <div className="modal-body p-0">
                        <div className="px-4 pt-3 pb-3 border-bottom bg-light">
                          <p className="small text-muted mb-3 mb-2">Company-wide public holidays (e.g. Swiss bank holidays). They appear on the Holiday calendar.</p>
                          <div className="d-flex flex-wrap align-items-end gap-3">
                            <div className="flex-shrink-0">
                              <label className="form-label small mb-1 text-secondary">Year</label>
                              <select
                                className="form-select form-select-sm"
                                style={{ minWidth: 110 }}
                                value={publicHolidayYear}
                                onChange={(e) => setPublicHolidayYear(Number(e.target.value))}
                              >
                                {[2025, 2026, 2027, 2028].map((y) => (
                                  <option key={y} value={y}>{y}</option>
                                ))}
                              </select>
                            </div>
                            <button type="button" className="btn btn-warning btn-sm" onClick={openAddPublicHoliday}>
                              <i className="bi bi-plus-lg me-1" /> Add holiday
                            </button>
                          </div>
                        </div>
                        <div className="px-4 py-3">
                          {loadingPublicHolidays ? (
                            <p className="text-muted small mb-0">Loading...</p>
                          ) : publicHolidays.length === 0 ? (
                            <p className="text-muted small mb-0">
                              <i className="bi bi-calendar-x me-2" />
                              No public holidays for {publicHolidayYear}. Use &quot;Add holiday&quot; above to add one.
                            </p>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-sm table-hover mb-0 hr-public-holidays-table">
                                <thead>
                                  <tr>
                                    <th className="hr-ph-date">Date</th>
                                    <th className="hr-ph-day">Day</th>
                                    <th className="hr-ph-name-en">Name (EN)</th>
                                    <th className="hr-ph-name-fr">Name (FR)</th>
                                    <th className="text-end hr-ph-actions">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {publicHolidays.map((h) => {
                                    const d = h.date ? new Date(h.date) : null;
                                    const endD = h.endDate ? new Date(h.endDate) : null;
                                    const dateStr = d ? d.toLocaleDateString(undefined, { dateStyle: 'medium' }) : '–';
                                    const endStr = endD ? endD.toLocaleDateString(undefined, { dateStyle: 'medium' }) : '';
                                    const rangeStr = endStr && endStr !== dateStr ? `${dateStr} – ${endStr}` : dateStr;
                                    const dayStr = d ? d.toLocaleDateString(undefined, { weekday: 'short' }) : '–';
                                    return (
                                      <tr key={h._id}>
                                        <td className="hr-ph-date">{rangeStr}</td>
                                        <td className="hr-ph-day">{dayStr}</td>
                                        <td className="hr-ph-name-en">{h.name || '–'}</td>
                                        <td className="hr-ph-name-fr text-muted">{h.nameFr || '–'}</td>
                                        <td className="text-end hr-ph-actions">
                                            <div className="d-flex gap-1 justify-content-end">
                                              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openEditPublicHoliday(h)}>Edit</button>
                                              <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => deletePublicHoliday(h._id)}>Remove</button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {publicHolidayModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">{publicHolidayModal.type === 'add' ? 'Add public holiday' : 'Edit public holiday'}</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => setPublicHolidayModal(null)} />
                      </div>
                      <div className="modal-body">
                        <div className="mb-3">
                          <label className="form-label small mb-1">Name (English)</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g. New Year's Day"
                            value={publicHolidayForm.name}
                            onChange={(e) => setPublicHolidayForm((f) => ({ ...f, name: e.target.value }))}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label small mb-1">Name (French)</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g. Nouvel An"
                                value={publicHolidayForm.nameFr}
                                onChange={(e) => setPublicHolidayForm((f) => ({ ...f, nameFr: e.target.value }))}
                              />
                            </div>
                        <div className="mb-3">
                              <label className="form-label small mb-1">Start date</label>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={publicHolidayForm.date}
                                onChange={(e) => setPublicHolidayForm((f) => ({ ...f, date: e.target.value }))}
                              />
                            </div>
                            <div>
                              <label className="form-label small mb-1">End date <span className="text-muted fw-normal">(optional, for multi-day e.g. Christmas break)</span></label>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={publicHolidayForm.endDate || ''}
                                onChange={(e) => setPublicHolidayForm((f) => ({ ...f, endDate: e.target.value }))}
                              />
                            </div>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPublicHolidayModal(null)}>Cancel</button>
                        <button type="button" className="btn btn-warning btn-sm" disabled={!publicHolidayForm.name.trim() || savingPublicHoliday} onClick={savePublicHoliday}>
                          {savingPublicHoliday ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </>
          ) : (
            <>
              {activeSection === 'overview' && (
                <>
                  <h2 className="hr-page-title">Dashboard</h2>
                  <p className="text-muted small mb-4">
                    Quick summary of leave, overtime, and pending requests.
                  </p>
                  {overviewError && <div className="alert alert-danger py-2">{overviewError}</div>}
                  {overviewLoading ? (
                    <div className="text-muted">Loading overview...</div>
                  ) : (
                    <>
                      <div className="employee-overview-section">
                        <div className="db-kpis db-kpis--five mb-4">
                          <div className="db-kpi">
                            <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-calendar2-week" /></div>
                            <div className="db-kpi-body">
                              <div className="db-kpi-val">{annualAllowanceView?.allowanceDays ?? 0}</div>
                              <div className="db-kpi-label">Annual leaves</div>
                            </div>
                          </div>
                          <div className="db-kpi">
                            <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-check2-square" /></div>
                            <div className="db-kpi-body">
                              <div className="db-kpi-val">{annualAllowanceView?.usedDays ?? 0}</div>
                              <div className="db-kpi-label">Used annual</div>
                            </div>
                          </div>
                          <div className="db-kpi">
                            <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-hourglass-split" /></div>
                            <div className="db-kpi-body">
                              <div className="db-kpi-val">{annualAllowanceView?.remaining ?? 0}</div>
                              <div className="db-kpi-label">Annual left</div>
                            </div>
                          </div>
                          <div className="db-kpi">
                            <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-clock-history" /></div>
                            <div className="db-kpi-body">
                              <div className="db-kpi-val">{employeeOverview.approvedOvertimeDays}</div>
                              <div className="db-kpi-label">Overtime left</div>
                            </div>
                          </div>
                          <div className="db-kpi">
                            <div className="db-kpi-icon db-kpi-icon--annual"><i className="bi bi-calculator" /></div>
                            <div className="db-kpi-body">
                              <div className="db-kpi-val">
                                {Math.round(((Number(annualAllowanceView?.remaining) || 0) + (Number(employeeOverview.approvedOvertimeDays) || 0)) * 100) / 100}
                              </div>
                              <div className="db-kpi-label">Total left</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="employee-overview-section">
                        <div className="db-pending db-pending--below mb-4">
                          <div className="db-pending-head">
                            <span className="db-pending-title">Pending requests</span>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <button type="button" className="db-pending-link border-0 bg-transparent p-0" onClick={() => setActiveSection('holidays')}>Holiday requests</button>
                              <span className="text-muted small">·</span>
                              <button type="button" className="db-pending-link border-0 bg-transparent p-0" onClick={() => setActiveSection('overtime')}>Overtime requests</button>
                            </div>
                          </div>
                          {pendingOverviewItems.length === 0 ? (
                            <p className="db-pending-empty">No pending requests</p>
                          ) : (
                            <div className="db-pending-list">
                              {pendingOverviewItems.map((item) => (
                                <div
                                  key={item.id}
                                  className="db-pending-item"
                                  onClick={() => setActiveSection(item.kind === 'overtime' ? 'overtime' : 'holidays')}
                                  role="button"
                                  tabIndex={0}
                                >
                                  <div className="db-pending-item-main">
                                    <div className="db-pending-item-meta">
                                      <span className="db-pending-item-name">{dn(me?.username) || 'You'}</span>
                                      <span className={`badge rounded-pill ${item.kind === 'overtime' ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                        {item.kind === 'overtime' ? 'Overtime' : 'Leave'}
                                      </span>
                                    </div>
                                    <span className="db-pending-item-date">{item.dateLabel}</span>
                                  </div>
                                  <i className="bi bi-chevron-right db-pending-item-arrow" />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="employee-overview-section">
                        <h3 className="employee-overview-section-title">Personal calendar</h3>
                        <div className="employee-range-calendar">
                          <div className="employee-range-calendar-head d-flex flex-wrap justify-content-end align-items-center gap-2 mb-1">
                            <div className="btn-group btn-group-sm" role="group" aria-label="Overview range">
                              <button
                                type="button"
                                className={`btn ${overviewRangePreset === 'this_week' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setOverviewRangePreset('this_week')}
                              >
                                This week
                              </button>
                              <button
                                type="button"
                                className={`btn ${overviewRangePreset === 'this_month' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setOverviewRangePreset('this_month')}
                              >
                                This month
                              </button>
                              <button
                                type="button"
                                className={`btn ${overviewRangePreset === 'next_two_weeks' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setOverviewRangePreset('next_two_weeks')}
                              >
                                Next 2 weeks
                              </button>
                            </div>
                          </div>
                          <div className="employee-range-subtle">
                            {overviewRange.label}: {formatDateShort(overviewRange.start)} - {formatDateShort(overviewRange.end)}
                          </div>
                          <div className="employee-range-days">
                            {overviewRangeDays.map((day) => (
                              <div
                                key={day.key}
                                className={`employee-range-day ${day.inRange ? '' : 'employee-range-day--out'} ${day.isToday ? 'employee-range-day--today' : ''} ${day.isWeekend ? 'employee-range-day--weekend' : ''} ${day.isOffDay ? 'employee-range-day--offday' : ''} ${day.publicHolidayName ? 'employee-range-day--holiday' : ''}`}
                              >
                                <div className="employee-range-day-head">
                                  <span>{day.label}</span>
                                  <strong>{day.dateText}</strong>
                                </div>
                                {day.publicHolidayName && <div className="employee-range-badge employee-range-badge--holiday">{day.publicHolidayName}</div>}
                                {day.leaveCount > 0 && <div className="employee-range-badge employee-range-badge--leave">Approved leave</div>}
                                {day.entry ? (
                                  <div className="employee-range-worked">
                                    <div>
                                      {day.entry.entryMode === 'time' && day.entry.startTime && day.entry.endTime
                                        ? `${formatHHmmToAmPm(day.entry.startTime)} – ${formatHHmmToAmPm(day.entry.endTime)}`
                                        : formatDecimalHours(day.entry.totalHours)}
                                    </div>
                                    {day.entry.entryMode === 'time' && (
                                      day.entry.breakStartTime && day.entry.breakEndTime
                                        ? <div className="employee-range-break">Break {formatHHmmToAmPm(day.entry.breakStartTime)} – {formatHHmmToAmPm(day.entry.breakEndTime)}</div>
                                        : getDerivedBreakHours(day.entry) != null && (
                                          <div className="employee-range-break">Break {formatDecimalHours(getDerivedBreakHours(day.entry))}</div>
                                        )
                                    )}
                                  </div>
                                ) : day.isWeekend ? (
                                  <div className="employee-range-worked employee-range-worked--muted">Weekend</div>
                                ) : day.isOffDay ? (
                                  <div className="employee-range-worked employee-range-worked--muted">Off day</div>
                                ) : day.publicHolidayName ? (
                                  <div className="employee-range-worked employee-range-worked--muted">Public holiday</div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              {activeSection === 'working-hours' && (
                <>
                  <h2 className="hr-page-title">Working hours</h2>
                  <WorkingHoursCalendar
                    applyEmployeeSubmissionLock
                    intro={
                      <p className="text-muted small mb-0">
                        Enter from/to times or hours per day.
                      </p>
                    }
                  />
                </>
              )}

              {activeSection === 'holidays' && (
                <>
                  <h2 className="hr-page-title">Holiday requests</h2>
                  <div className="hr-section-card">
                    {(() => {
                      const annualUsed = annualAllowanceView?.usedDays ?? 0;
                      const annualRemaining = annualAllowanceView?.remaining ?? 0;
                      const otBalance = employeeOverview.overtimeBalance;
                      const totalUsed = Math.round((annualUsed + otBalance.usedDays) * 100) / 100;
                      const totalRemaining = Math.round((annualRemaining + otBalance.remaining) * 100) / 100;
                      return (
                        <div className="mb-4 rounded" style={{ border: '1px solid rgba(31, 78, 121, 0.15)', overflow: 'hidden' }}>
                          <div className="px-4 py-3" style={{ backgroundColor: 'rgba(31, 78, 121, 0.06)' }}>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                              <div>
                                <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, color: 'rgba(0,87,60,0.6)', marginBottom: 4 }}>Total leave balance</div>
                                <div className="d-flex align-items-baseline gap-2">
                                  <span style={{ fontSize: '1.4rem', fontWeight: 700, color: 'rgba(0,87,60,0.85)', lineHeight: 1 }}>{totalRemaining}</span>
                                  <span className="text-muted" style={{ fontSize: '0.82rem' }}>days remaining</span>
                                  <span className="text-muted" style={{ fontSize: '0.82rem' }}>·</span>
                                  <span style={{ fontSize: '0.82rem', color: '#888' }}>{totalUsed} used</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm d-flex align-items-center gap-1"
                                style={{ fontSize: '0.75rem', color: '#666', border: '1px solid rgba(0,0,0,0.13)', backgroundColor: 'transparent', padding: '4px 10px', borderRadius: 6 }}
                                onClick={() => setShowBalanceBreakdown((v) => !v)}
                                aria-expanded={showBalanceBreakdown}
                              >
                                {showBalanceBreakdown ? 'Hide' : 'Breakdown'}
                                <i className={`bi bi-chevron-${showBalanceBreakdown ? 'up' : 'down'}`} style={{ fontSize: '0.65rem' }} />
                              </button>
                            </div>
                          </div>
                          {showBalanceBreakdown && (
                            <div style={{ backgroundColor: '#fff', borderTop: '1px solid rgba(0,87,60,0.10)' }}>
                              {[
                                { label: 'Annual leave', used: annualUsed, remaining: annualRemaining },
                                { label: 'Overtime leave', used: otBalance.usedDays, remaining: otBalance.remaining },
                              ].map((row, i) => (
                                <div
                                  key={row.label}
                                  className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-4 py-2"
                                  style={{ borderTop: i > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(0,87,60,0.35)', display: 'inline-block', flexShrink: 0 }} />
                                    <span style={{ fontSize: '0.8rem', color: '#555' }}>{row.label}</span>
                                  </div>
                                  <div className="d-flex align-items-center gap-3">
                                    <span style={{ fontSize: '0.8rem', color: '#444' }}><strong>{row.remaining}</strong> <span style={{ color: '#888' }}>remaining</span></span>
                                    <span style={{ fontSize: '0.78rem', color: '#aaa' }}>{row.used} used</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <h3 className="mb-3">Submit & track requests</h3>
                    <div className="rounded p-3 mb-4" style={{ backgroundColor: 'rgba(31, 78, 121, 0.06)', border: '1px solid rgba(31, 78, 121, 0.12)' }}>
                      {holidaySuccessMessage && (
                        <div className="alert alert-success alert-dismissible py-2 mb-2" role="status" aria-live="polite">
                          <span>{holidaySuccessMessage}</span>
                          <button
                            type="button"
                            className="btn-close"
                            aria-label="Dismiss"
                            onClick={() => setHolidaySuccessMessage('')}
                          />
                        </div>
                      )}
                      {holidayError && <div className="alert alert-danger py-2 mb-2">{holidayError}</div>}
                      <HolidayRequestForm onSubmit={handleHolidaySubmit} loading={holidayLoading} />
                    </div>
                    <h4 className="mb-2">Your requests</h4>
                    <HolidayRequestList requests={holidayRequests} onUpdate={fetchMyHolidays} />
                  </div>
                </>
              )}
              {activeSection === 'overtime' && (
                <>
                  <h2 className="hr-page-title">Overtime requests</h2>
                  <div className="hr-section-card">
                    <h3 className="mb-3">Submit & track requests</h3>
                    <div className="rounded p-3 mb-4" style={{ backgroundColor: 'rgba(31, 78, 121, 0.06)', border: '1px solid rgba(31, 78, 121, 0.12)' }}>
                      {overtimeEmployeeSuccessMessage && (
                        <div className="alert alert-success alert-dismissible py-2 mb-2" role="status" aria-live="polite">
                          <span>{overtimeEmployeeSuccessMessage}</span>
                          <button
                            type="button"
                            className="btn-close"
                            aria-label="Dismiss"
                            onClick={() => setOvertimeEmployeeSuccessMessage('')}
                          />
                        </div>
                      )}
                      {overtimeEmployeeError && <div className="alert alert-danger py-2 mb-2">{overtimeEmployeeError}</div>}
                      <OvertimeRequestForm onSubmit={handleOvertimeSubmit} loading={overtimeEmployeeLoading} />
                    </div>
                    <h4 className="mb-2">Your overtime requests</h4>
                    <OvertimeRequestList requests={overtimeEmployeeRequests} onUpdate={fetchMyOvertime} />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default ManagerDashboard;
